import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { CustomerNotesPanel } from "@/components/CustomerNotesPanel";
import { Label } from "@/components/ui/label";
// Input import removed in iter179 — last inline use lived in the
// Email Dialog, which moved to WOEmailDialog.jsx.
// Textarea import removed in iter178 — only inline use was the
// Internal Notes block, which moved to WOInternalNotes.jsx.
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignaturePad } from "@/components/SignaturePad";
// PushToFoundationButton import removed in iter180 — moved to WOActionsRow.jsx.
// Label + SearchableDropdown imports removed in iter182 — moved with
// the Equipment Library picker to WOEquipmentLibraryPicker.jsx.
// iter182 cleanup: trimmed lucide-react from 14 icons to just 3
// still used directly. 11 icons moved to their new owner
// components (Trash2 → WOListTable; the rest were already orphaned
// by previous tab/section extractions and were only referenced
// inside comments/string literals).
import { toast } from "sonner";
import { format } from "date-fns";
import { getMountainToday, getMountainNow } from "@/lib/dateUtils";
import { NumericFormat } from "react-number-format";
// Dialog primitives removed in iter179 — the WO Preview is a custom
// modal and the Email Dialog moved to WOEmailDialog.jsx.
// Badge import removed in iter177 — only the inline `costBadgeColor`
// className map was using it, and the actual Badge JSX moved to
// `WOInspectionsList`. The map is just a color lookup, not a Badge.
import {
  getWorkOrders, getWorkOrder, getNextWONumber, createWorkOrder, updateWorkOrder, patchWorkOrderStatus, signWorkOrder, deleteWorkOrder,
  getManufacturers, getEquipmentTypes, getParts,
  getMasterScopes, getMasterEquipment, getMasterParts,
  getInspectionTemplates, getInspections, createInspection, deleteInspection,
  lookupTaxRate, getTechnicians, getProjectManagers, getAccountManagers,
  submitChangeOrder, revertCOToDraft, approveChangeOrder, unapproveChangeOrder, notApproveChangeOrder,
  getWOInvoices, createInvoice, updateInvoice, deleteInvoice,
  // uploadExecutedWO + getExecutedWODocument moved to WOExecutedDoc.jsx (iter178)
  // pushWorkOrderToFoundation moved to WOActionsRow.jsx (iter180)
  // pushChangeOrderToFoundation orphaned — was only used by the
  // inline CO Foundation push (now part of WOChangeOrdersSection
  // via its onPush callbacks). Removed in iter180 cleanup.
  listDispatches, getSites, getSettings,
} from "@/lib/api";
import PurchaseOrders from "@/components/PurchaseOrders";
// InvoiceSection import removed in iter179 — moved to WOInvoicesPanel.jsx.
import WOPreview from "@/components/WOPreview";
// COBuilder import removed in iter179 — moved to WOChangeOrdersSection.jsx.
// ExecutedDocDropZone import removed in iter178 — now lives in
// WOExecutedDoc.jsx along with the upload/view API helpers.
import { TechInfoSection } from "@/components/TechInfoSection";
import { generateWOReviewToken, ensureWOShortCode } from "@/lib/api";
import { downloadPdfUrl } from "@/lib/downloadBlob";
// iter170 component split (step #1): pure helpers + constants moved
// to `workOrderTab/workOrderTabUtils.js` to start trimming this file
// down from 2,908 LOC. Anything imported below is exclusively pure
// (no component state, no side effects).
import {
  STATUS_LABELS,
  STATUS_COLORS,
  COST_TYPES,
  COST_TYPE_MAP,
  costTypeLabel,
  normalizeCostType,
  round2,
  // addBusinessDays moved to WODatesRow.jsx (iter178)
  WORKFLOW_STEPS,
  emptyLineItem,
  cleanDescription,
  consolidateLineItems,
} from "./workOrderTab/workOrderTabUtils";
import { rollUpLineItems, applyCCFeeToRolledItems } from "@/lib/rollUpLineItems";
import { foldHiddenTaxIntoRows, NO_MATERIAL_MSG } from "@/lib/hideTax";
import { useWOAutoSave } from "./workOrderTab/useWOAutoSave";
import { WOFormHeader } from "./workOrderTab/WOFormHeader";
import { WOAddContactDialog } from "./workOrderTab/WOAddContactDialog";
import { WOAddInspectionDialog } from "./workOrderTab/WOAddInspectionDialog";
import { WOSignatureBlock } from "./workOrderTab/WOSignatureBlock";
import { WOPeopleCluster } from "./workOrderTab/WOPeopleCluster";
import { WOFlagsBar } from "./workOrderTab/WOFlagsBar";
import { nextCoNumber } from "@/lib/coNumber";
import { TMChargesPanel } from "./workOrderTab/TMChargesPanel";
import { WOLineItemsTable } from "./workOrderTab/WOLineItemsTable";
// WO*Actions + WORestoreButton imports removed in iter180 — used
// only inside WOActionsRow.jsx now.
import { WOQualificationsSection } from "./workOrderTab/WOQualificationsSection";
import { WOTotalsRow } from "./workOrderTab/WOTotalsRow";
import { WOFoundationActualsCard } from "./workOrderTab/WOFoundationActualsCard";
import { WOInspectionsList } from "./workOrderTab/WOInspectionsList";
import { WODatesRow } from "./workOrderTab/WODatesRow";
import { WOInternalNotes } from "./workOrderTab/WOInternalNotes";
import { WONoteLog } from "./workOrderTab/WONoteLog";
import { WOExecutedDoc } from "./workOrderTab/WOExecutedDoc";
import { WOChangeOrdersSection } from "./workOrderTab/WOChangeOrdersSection";
import { WOEmailDialog } from "./workOrderTab/WOEmailDialog";
import { COEmailDialog } from "./workOrderTab/COEmailDialog";
import { WOInvoicesPanel } from "./workOrderTab/WOInvoicesPanel";
import { WOActionsRow } from "./workOrderTab/WOActionsRow";
import { WOFieldNotes } from "./workOrderTab/WOFieldNotes";
import { WOApprovedCOLineItems } from "./workOrderTab/WOApprovedCOLineItems";
import { WOListTable } from "./workOrderTab/WOListTable";
import { WOAddressLink } from "./workOrderTab/WOAddressLink";
import { WOEquipmentLibraryPicker } from "./workOrderTab/WOEquipmentLibraryPicker";
import { WOSourceProposalBadge } from "./workOrderTab/WOSourceProposalBadge";
import { WOHeaderCard } from "./workOrderTab/WOHeaderCard";
import { QuickWOModal } from "./workOrderTab/QuickWOModal";
import { WOStatusWorkflow } from "./workOrderTab/WOStatusWorkflow";
import { WOActivityTrail } from "./workOrderTab/WOActivityTrail";
import { useEmailDocMode, docFilename, withDocName } from "@/components/common/EmailDocMode";
import { WOGeneralCard } from "./workOrderTab/WOGeneralCard";
import { WODirectoryCard } from "./workOrderTab/WODirectoryCard";
import { WOBudgetCard, WOFinancialSummaryCard } from "./workOrderTab/WOBudgetCard";
import { WOLineItemsActions } from "./workOrderTab/WOLineItemsActions";
import SubTabs from "@/components/common/SubTabs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addCompanyHeader } from "@/lib/pdfHeader";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import { useAuth } from "@/auth/AuthContext";
import FoundationCompletionPrompt from "@/components/integrations/FoundationCompletionPrompt";
// abbreviateName + formatNoteDate imports removed in iter180 —
// moved with the Field Notes accordion to WOFieldNotes.jsx.
// SearchableDropdown import removed in iter182 — moved with the
// Equipment Library picker to WOEquipmentLibraryPicker.jsx.

// Defined in workOrderTab/workOrderTabUtils.js (extracted iter170 to
// trim this file's 2,908 LOC and centralize the pure helpers):
//   STATUS_LABELS, STATUS_COLORS, COST_TYPES, COST_TYPE_MAP,
//   costTypeLabel, normalizeCostType, round2, addBusinessDays,
//   WORKFLOW_STEPS, emptyLineItem, cleanDescription,
//   consolidateLineItems. See the top-of-file import block.

// UI sweep batch 4 — saved WOs are tabbed (user choice: fewer tabs; Field = inspections + tech info).
const WO_TABS = [
  { key: "info", label: "Info", testId: "wo-tab-btn-info" },
  { key: "line_items", label: "Line Items", testId: "wo-tab-btn-line-items" },
  { key: "change_orders", label: "Change Orders", testId: "wo-tab-btn-change-orders" },
  { key: "purchase_orders", label: "Purchase Orders", testId: "wo-tab-btn-purchase-orders" },
  { key: "invoices", label: "Invoices", testId: "wo-tab-btn-invoices" },
  { key: "field", label: "Field", testId: "wo-tab-btn-field" },
  { key: "activity", label: "Activity", testId: "wo-tab-btn-activity" },
];


const WorkOrderTab = forwardRef(function WorkOrderTab({ clientId, clientName, clientSites, clientEquipment, getSiteNameById, selectedSiteId, openWorkOrderId, onWorkOrderOpened, copyFromWO, onCopyHandled, viewFromWO, onViewHandled, returnTo, onReturnHandled, clientHideTax, clientBillingContact, clientCustomerNumber, clientCustomerType, fullPage = false, onClose, initialWoTab = null }, ref) {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingWO, setEditingWO] = useState(null);
  const [signedBy, setSignedBy] = useState("");
  const [allParts, setAllParts] = useState([]);
  const [masterScopes, setMasterScopes] = useState([]);
  const [masterEquipmentList, setMasterEquipmentList] = useState([]);
  const [masterPartsList, setMasterPartsList] = useState([]);
  const [allEquipTypes, setAllEquipTypes] = useState([]);
  const [allManufacturers, setAllManufacturers] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showWOPreview, setShowWOPreview] = useState(false);
  const [expandedWOCOs, setExpandedWOCOs] = useState({});
  const [technicians, setTechnicians] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [selectedContactEmail, setSelectedContactEmail] = useState("");
  const [emailType, setEmailType] = useState("submit"); // "submit" or "signed"

  // CO Email Dialog state — mirrors ProposalEmailDialog architecture so
  // the mailto: navigation fires from a FRESH user click in the dialog
  // (no async awaits before it). See COEmailDialog.jsx for the rationale.
  const [showCOEmailDialog, setShowCOEmailDialog] = useState(false);
  const [coEmailContext, setCoEmailContext] = useState(null);
  // { co, coAmount, approvalToken }
  const [coEmailSelectedContactId, setCoEmailSelectedContactId] = useState("");
  const [coEmailSelectedContactEmail, setCoEmailSelectedContactEmail] = useState("");

  // Inspection state
  const [inspectionTemplates, setInspectionTemplates] = useState([]);
  const [woInspections, setWoInspections] = useState([]);
  const [showAddInspDialog, setShowAddInspDialog] = useState(false);
  const [addInspForm, setAddInspForm] = useState({ template_id: "", inspector_name: "" });
  // UI sweep batch 5 — a saved WO is a full page: header card + Info · Line items · Change orders · Purchase orders · Invoices · Field · Activity.
  // Every WO lands on Info; a Cmd+K deep-link to a PO / invoice still expands it once that tab is opened.
  // Split is sticky per user (browser-local): on stays on (Info | Line items by default), off stays off.
  const { can: canAccess, user } = useAuth();
  const splitKey = `wo-split:${user?.id || user?.email || "anon"}`;
  const [woTab, setWoTab] = useState("info");
  // Full-page WO: tab ↔ ?tab= so Back/Forward move between tabs and a refresh keeps the tab.
  const [woSearch, setWoSearch] = useSearchParams();
  const urlTab = woSearch.get("tab");
  useEffect(() => {
    if (!fullPage || !urlTab || urlTab === woTab || !WO_TABS.some((t) => t.key === urlTab)) return;
    setWoTab(urlTab);
  }, [urlTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!fullPage || !woTab || woTab === urlTab) return;
    setWoSearch((p) => { const n = new URLSearchParams(p); n.set("tab", woTab); return n; }, { replace: !urlTab });
  }, [woTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [generalEditing, setGeneralEditing] = useState(false);
  const [fdnActuals, setFdnActuals] = useState(null); // Foundation snapshot → Budget "Actual" columns
  const [splitTab, setSplitTab] = useState(null); // second pane's tab when Split is on
  const [emailDocMode, setEmailDocMode] = useEmailDocMode(); // link vs download & attach (sticky, app-wide)
  useEffect(() => {
    let pref = null;
    try { pref = JSON.parse(localStorage.getItem(splitKey) || "null"); } catch { pref = null; }
    setWoTab(initialWoTab || (pref?.on ? (pref.left || "info") : "info"));
    setSplitTab(pref?.on ? (pref.right || "line_items") : null);
    setGeneralEditing(false); setFdnActuals(null);
  }, [editingWO?.id, splitKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!editingWO?.id) return;
    try { localStorage.setItem(splitKey, JSON.stringify({ on: !!splitTab, left: woTab, right: splitTab })); } catch { /* ignore */ }
  }, [splitTab, woTab, splitKey, editingWO?.id]);
  // "Schedule" with no date yet → open General in edit mode and focus the scheduled-date input.
  const requestScheduleDate = () => {
    setWoTab("info"); setGeneralEditing(true);
    setTimeout(() => {
      const el = scheduledDateRef.current;
      if (!el) return;
      el.focus();
      try { el.showPicker && el.showPicker(); } catch { /* focus is the fallback */ }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.info("Pick a date to schedule this work order");
    }, 150);
  };
  const [editingCompletionDate, setEditingCompletionDate] = useState(false);
  const [confirmWOComplete, setConfirmWOComplete] = useState(false);
  // Qualifications expansion — auto-hides when the WO has no
  // customer-visible notes saved, with a small "+ Add Qualifications"
  // toggle to bring the textarea back. Once a user types into it, it
  // stays open during the form session.
  const [showQualifications, setShowQualifications] = useState(false);
  const [coSaving, setCoSaving] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ name: "", phone: "", email: "", role: "" });
  // Extra contacts added in-session — keyed by site_id. Layered on top of
  // `clientSites[].contacts` so newly-created people show up in the dropdown
  // without waiting for a parent refresh.
  const [siteContactsOverlay, setSiteContactsOverlay] = useState({});

  const [form, setForm] = useState({
    title: "", description: "", site_id: "", work_date: getMountainToday(),
    equipment_ids: [], line_items: [emptyLineItem("labor", "Labor", 1, 150)],
    tax_rate: 0, hide_tax: false, tax_breakdown: null, tax_authority: "", customer_po_number: "", cc_fee_pct: 0, project_type: "service", tm_charges: [], account_manager: localStorage.getItem("abs_current_user") || "", account_manager_email: localStorage.getItem("abs_current_user_email") || "", site_contact_id: "", notes: [], inspection_items: [], wo_number: "", status: "draft", annual_service: false, scheduled_date: "", completion_date: "",
    proposal_id: null, budget: null, change_orders: [], assigned_tech_id: null, assigned_tech_ids: [], lead_tech_id: null, signature_required: true,
    project_manager_id: null, project_manager_name: "",
  });

  const loadWorkOrders = useCallback(async () => {
    try {
      if (!clientId && openWorkOrderId) {
        // WO with no customer attached (legacy / test rows): load it directly instead of an empty client list.
        const res = await getWorkOrder(openWorkOrderId);
        setWorkOrders(res.data ? [res.data] : []);
        return;
      }
      const res = await getWorkOrders({ client_id: clientId }); setWorkOrders(res.data);
    } catch {}
  }, [clientId, openWorkOrderId]);

  // Active WOs (exclude completed/cancelled - those live in History)
  const activeWorkOrders = useMemo(() => workOrders.filter(wo => wo.status !== "completed" && wo.status !== "cancelled"), [workOrders]);

  const loadReferenceData = useCallback(async () => {
    try {
      const [partsRes, typesRes, mfgRes, mScopesRes, mEquipRes, mPartsRes, inspTplRes] = await Promise.all([
        getParts(), getEquipmentTypes(), getManufacturers(),
        getMasterScopes(), getMasterEquipment({}), getMasterParts({}),
        getInspectionTemplates(),
      ]);
      setAllParts(partsRes.data); setAllEquipTypes(typesRes.data); setAllManufacturers(mfgRes.data);
      setMasterScopes(mScopesRes.data); setMasterEquipmentList(mEquipRes.data); setMasterPartsList(mPartsRes.data);
      setInspectionTemplates(inspTplRes.data);
    } catch {}
  }, []);

  useEffect(() => { if (clientId || openWorkOrderId) { loadWorkOrders(); loadReferenceData(); } }, [clientId, openWorkOrderId, loadWorkOrders, loadReferenceData]);
  useEffect(() => { getTechnicians().then(r => setTechnicians(r.data)).catch(() => {}); getProjectManagers().then(r => setProjectManagers(r.data)).catch(() => {}); getAccountManagers().then(r => setAccountManagers(r.data)).catch(() => {}); }, []);
  // Default CC Fee % from Settings → Default Rates. Seeds new WOs only —
  // existing WOs keep their saved cc_fee_pct via openEdit.
  // #140 (Group G): clamp to [0,100] and ignore non-finite values so a
  // corrupted settings blob can't push absurd fees into a new WO.
  const [defaultCcFeePct, setDefaultCcFeePct] = useState(0);
  // T&M default labor rate — same billed rate the Settings tab shows
  // ("Total Labor Rate" = ceil(default_labor_cost / (1 - GM%))), i.e.
  // identical to proposal labor pricing. Falls back to $155/hr.
  const [defaultLaborRate, setDefaultLaborRate] = useState(155);
  useEffect(() => {
    getSettings().then(r => {
      const v = parseFloat(r.data?.default_cc_fee_pct);
      if (Number.isFinite(v)) {
        const clamped = Math.min(100, Math.max(0, v));
        setDefaultCcFeePct(clamped);
      }
      const cost = parseFloat(r.data?.default_labor_cost);
      const gm = parseFloat(r.data?.default_labor_margin);
      if (Number.isFinite(cost) && cost > 0) {
        const rate = Number.isFinite(gm) && gm < 100 ? Math.ceil(cost / (1 - gm / 100)) : cost;
        if (Number.isFinite(rate) && rate > 0) setDefaultLaborRate(rate);
      }
    }).catch(() => {});
  }, []);

  // Refresh on tab focus — catches external updates (e.g. customer signed via review link)
  useEffect(() => {
    const refresh = async () => {
      if (document.visibilityState !== "visible" || !clientId) return;
      loadWorkOrders();
      if (editingWO?.id) {
        try {
          const res = await getWorkOrder(editingWO.id);
          const fresh = res.data;
          // Only update if server has meaningful changes (status/signature)
          if (fresh && (fresh.status !== editingWO.status || fresh.signed_at !== editingWO.signed_at)) {
            setEditingWO(fresh);
            setForm(prev => ({
              ...prev,
              status: fresh.status,
              signature_data: fresh.signature_data || "",
              signed_by: fresh.signed_by || "",
              signed_at: fresh.signed_at || "",
              signature_required: fresh.signature_required || false,
            }));
            if (fresh.status === "approved" && editingWO.status !== "approved") {
              toast.success("Work Order approved by customer");
            }
          }
        } catch { /* silent */ }
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [clientId, editingWO, loadWorkOrders]);

  // Escape key closes preview
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape" && showWOPreview) { setShowWOPreview(false); e.preventDefault(); } };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [showWOPreview]);

  // Auto-save WO (debounced, only for existing WOs) — extracted to
  // `useWOAutoSave` hook in iter170 (component split step #2). The
  // hook owns the timer, the first-load gate, and the save state
  // machine. `resetFirstLoad()` is exposed so openEdit/openCopy
  // flows can suppress the next autosave fire after they forcibly
  // reset `form`.
  const scheduledDateRef = useRef(null);
  const { woSaveStatus, resetFirstLoad: resetWOAutoSaveFirstLoad } = useWOAutoSave({ form, editingWO, clientId });
  // Parts-counter orders have no jobsite — they ship to the customer's
  // main address, so Site stays optional for them.
  const isPartsOnly = clientCustomerType === "parts_counter" || !!editingWO?.is_parts_only;


  // Auto-open a specific work order if navigated from dashboard.
  // Guarded per-id: the WorkOrders page slideout keeps `openWorkOrderId`
  // set for the whole session, so without this ref any `workOrders`
  // refetch (e.g. the window-focus refresh) re-ran openEdit() — wiping
  // in-progress edits and scrolling the sheet back to the top.
  const autoOpenedIdRef = useRef(null);
  useEffect(() => {
    if (openWorkOrderId && workOrders.length > 0) {
      if (autoOpenedIdRef.current === openWorkOrderId) return;
      const wo = workOrders.find(w => w.id === openWorkOrderId);
      if (wo) {
        autoOpenedIdRef.current = openWorkOrderId;
        openEdit(wo);
        if (onWorkOrderOpened) onWorkOrderOpened();
        // Deep-link UX (iter229): the WO editor renders below the client
        // sheet header — without this scroll the user lands on the
        // customer info and thinks they were sent "to the customer page".
        if (!fullPage) setTimeout(() => {
          document.querySelector('[data-testid="work-order-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 450);
      }
    }
    // Triggered only on incoming navigation hook — `openEdit` and the
    // `onWorkOrderOpened` callback are stable for this lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWorkOrderId, workOrders]);

  // Handle copy from History tab via parent state
  useEffect(() => {
    if (copyFromWO) {
      openCopy(copyFromWO);
      if (onCopyHandled) onCopyHandled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyFromWO]);

  // Handle view from History tab via parent state
  useEffect(() => {
    if (viewFromWO) {
      if (fullPage) openEdit(viewFromWO); else navigate(`/work-orders/${viewFromWO.id}`);
      if (onViewHandled) onViewHandled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFromWO]);

  // Auto-lookup the jurisdiction tax rate when the site changes
  // Double-tap on the read-only tax chip enters an inline override mode
  // — PM can punch in a custom rate without changing tax-exempt status
  // or the underlying decision tree. Override persists for this WO only.
  const [taxOverride, setTaxOverride] = useState(false);
  const taxOverrideOpenedAtRef = useRef(0);
  const openTaxOverride = useCallback(() => {
    // Defer state flip past the dblclick event boundary so the freshly
    // mounted Input keeps focus — without this, dblclick aftermath blurs
    // the input and collapses override mode in the same frame.
    taxOverrideOpenedAtRef.current = Date.now();
    requestAnimationFrame(() => setTaxOverride(true));
  }, []);
  const closeTaxOverride = useCallback(() => {
    if (Date.now() - taxOverrideOpenedAtRef.current < 200) return;
    setTaxOverride(false);
  }, []);
  // Also re-fires when the site's address (city/state) changes so a WO
  // whose site moves jurisdictions stays in sync. Reading the address
  // strings (not just the id) into the dep array ensures the lookup
  // refreshes the moment the user edits the site under Customers.
  const prevTaxKeyRef = useRef(null);
  // Customer tax override returned by the lookup (Settings → Tax
  // Decision Tree → Step 0). Kept in component state, not in `form`,
  // so a WO re-hydration can't clobber the badge/lock state.
  const [custTaxOverride, setCustTaxOverride] = useState(null);
  const poRefreshRef = useRef(null);
  const taxLookupSite = useMemo(() => {
    if (!form.site_id) return null;
    const s = clientSites.find(x => x.id === form.site_id);
    return s ? { city: s.city || "", state: s.state || "" } : null;
  }, [form.site_id, clientSites]);
  useEffect(() => {
    if (!form.site_id || !showForm || !taxLookupSite) return;
    if (!taxLookupSite.state) return;
    // iter306 — a manual tax (override or sticky manual rate) owns the
    // rate; the jurisdiction lookup must never clobber it.
    if (form.tax_override || form.tax_rate_manual) return;
    const key = `${clientId}_${form.site_id}_${taxLookupSite.city}_${taxLookupSite.state}`;
    if (prevTaxKeyRef.current === key) return;
    prevTaxKeyRef.current = key;
    // #163 (Group G): jurisdiction-change must override stale rate.
    // iter310 — one path only: the site jurisdiction rate always applies.
    // Whether the customer SEES it is the display-only `hide_tax` flag.
    lookupTaxRate({ city: taxLookupSite.city, state: taxLookupSite.state, client_id: clientId })
      .then(res => { setCustTaxOverride(res.data.customer_override || null); setForm(prev => ({ ...prev, tax_rate: res.data.total_rate, tax_breakdown: res.data.breakdown, tax_authority: res.data.tax_no || prev.tax_authority })); })
      .catch(() => {});
  }, [form.site_id, showForm, taxLookupSite, clientId, form.tax_override, form.tax_rate_manual]);

  // Auto-default the Site Contact when the site changes (or on first open
  // for a fresh WO). Uses the site's primary contact; if the site has no
  // primary, picks the first contact. Does NOT overwrite an existing pick.
  useEffect(() => {
    if (!showForm || !form.site_id || form.site_contact_id) return;
    const site = clientSites.find(s => s.id === form.site_id);
    const contacts = (site?.contacts) || [];
    const primary = contacts.find(c => c.is_primary) || contacts[0];
    if (primary?.id) {
      setForm(prev => prev.site_contact_id ? prev : { ...prev, site_contact_id: primary.id });
    }
  }, [form.site_id, form.site_contact_id, showForm, clientSites]);


  const loadWoInspections = useCallback(async (woId) => {
    if (!woId) { setWoInspections([]); return; }
    try {
      const res = await getInspections({ work_order_id: woId });
      setWoInspections(res.data);
    } catch { setWoInspections([]); }
  }, []);

  // Lines may carry `qty` (older fixtures) instead of `quantity`; never let a NaN reach the totals.
  const subtotal = useMemo(() => (form.line_items || []).reduce((sum, i) => sum + ((Number(i.quantity ?? i.qty) || 0) * (Number(i.unit_price) || 0)), 0), [form.line_items]);
  const coSubtotal = useMemo(() => (form.change_orders || []).filter(co => co.status === "approved").reduce((sum, co) => sum + (co.subtotal || 0), 0), [form.change_orders]);
  const hasApprovedCOs = (form.change_orders || []).some(co => co.status === "approved");
  const revisedSubtotal = subtotal + coSubtotal;
  // CC Fee — Square processing fee (defaults to 3.5%). Categorized as Material so it's taxed.
  const ccFeePct = form.cc_fee_pct == null ? 0 : (parseFloat(form.cc_fee_pct) || 0);
  const ccFeeAmount = round2(revisedSubtotal * (ccFeePct / 100));
  // Tax applies to material only. CC fee is treated as material, so it's included in the tax base.
  // iter306 — a CO with its own manual tax is priced on its own and added
  // to the WO tax; a WO-level manual tax (carried from the proposal) prices
  // the WO's own base. Mirrors routes/work_orders._recompute_wo_totals.
  const { materialSubtotal, coOverrideTax } = useMemo(() => {
    const isMaterial = (ct) => { const s = String(ct || "").toLowerCase(); return s === "2" || s === "material"; };
    const woRate = parseFloat(form.tax_rate) || 0;
    let mat = 0;
    let coTax = 0;
    (form.line_items || []).forEach(li => { if (isMaterial(li.cost_type)) mat += (li.quantity || 0) * (li.unit_price || 0); });
    (form.change_orders || []).filter(co => co.status === "approved").forEach(co => {
      let coMat = 0;
      (co.line_items || []).forEach(li => { if (isMaterial(li.cost_type)) coMat += (li.quantity || 0) * (li.unit_price || 0); });
      const ov = co.tax_override;
      if (ov?.type === "rate") coTax += coMat * ((parseFloat(ov.rate) || 0) / 100);
      else if (ov?.type === "amount") coTax += parseFloat(ov.amount) || 0;
      else mat += coMat;
    });
    return { materialSubtotal: mat, coOverrideTax: coTax };
  }, [form.line_items, form.change_orders, form.tax_rate]);
  const taxableBase = materialSubtotal + ccFeeAmount;
  const woTaxOverride = form.tax_override || null;
  const baseTax = woTaxOverride?.type === "amount"
    ? (parseFloat(woTaxOverride.amount) || 0)
    : taxableBase * ((woTaxOverride?.type === "rate" ? (parseFloat(woTaxOverride.rate) || 0) : (parseFloat(form.tax_rate) || 0)) / 100);
  const woTaxStale = woTaxOverride?.type === "amount" && woTaxOverride.basis_taxable != null
    && Math.abs(Number(woTaxOverride.basis_taxable) - materialSubtotal) > 0.01;
  const taxAmount = baseTax + coOverrideTax;
  const total = revisedSubtotal + ccFeeAmount + taxAmount;

  const updateLineItem = (idx, field, value) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [field]: value };
    // Track base_quantity when user directly edits a labor line's hours
    if (field === "quantity" && normalizeCostType(items[idx].cost_type) === "1") {
      items[idx].base_quantity = value;
    }
    // If quantity changes on a material/equipment/freight line, scale linked labor lines
    if (field === "quantity" && normalizeCostType(items[idx].cost_type) !== "1") {
      const baseDesc = items[idx].description;
      const oldQty = form.line_items[idx].quantity || 1;
      const newQty = value || 1;
      if (oldQty !== newQty) {
        items.forEach((li, i) => {
          if (i !== idx && normalizeCostType(li.cost_type) === "1" && li.description === baseDesc) {
            // base_quantity = per-unit hours. Derive from current state if not stored.
            const baseHrs = li.base_quantity || (li.quantity / oldQty);
            items[i] = { ...li, quantity: Math.round(baseHrs * newQty * 100) / 100, base_quantity: baseHrs };
            items[i].total = items[i].quantity * items[i].unit_price;
          }
        });
      }
    }
    items[idx].total = items[idx].quantity * items[idx].unit_price;
    setForm({ ...form, line_items: items });
  };
  const removeLineItem = (idx) => { if ((form.line_items || []).length <= 1) return; setForm({ ...form, line_items: (form.line_items || []).filter((_, i) => i !== idx) }); };

  // Equipment Library search items for the dropdown — Name first, Part# second
  const librarySearchItems = useMemo(() => {
    const items = [];
    masterPartsList.forEach(mp => {
      const equip = masterEquipmentList.find(e => e.id === mp.equipment_id);
      const scope = masterScopes.find(s => s.id === mp.scope_id);
      const equipName = equip?.name || "";
      const scopeName = scope?.name || "";
      const label = `${mp.name}${mp.part_number ? ` [${mp.part_number}]` : ""} — ${equipName}${scopeName ? ` (${scopeName})` : ""}`;
      items.push({ id: `part:${mp.id}`, name: label, _part: mp, _equipName: equipName });
    });
    masterEquipmentList.forEach(me => {
      const scope = masterScopes.find(s => s.id === me.scope_id);
      const label = `${me.name}${me.description ? ` — ${me.description}` : ""}${scope ? ` (${scope.name})` : ""}`;
      items.push({ id: `equip:${me.id}`, name: label, _equip: me });
    });
    return items;
  }, [masterPartsList, masterEquipmentList, masterScopes]);

  // Add library item as WO line item (uses Gross Margin pricing)
  const addLibraryItem = (item) => {
    if (item.id.startsWith("part:")) {
      const mp = item._part;
      const equipName = item._equipName;
      // Window Care / subcontract pass-through: customer price is flat_rate,
      // the sub's number rides along as subcontract_cost on a cost-type 3 line.
      if (mp.pricing_mode === "subcontract") {
        const price = Math.round((parseFloat(mp.flat_rate) || 0) * 100) / 100;
        const subItem = emptyLineItem("subcontract", mp.name, 1, price, mp.part_number || "", mp.manufacturer || "");
        subItem.subcontract_cost = Math.round((parseFloat(mp.subcontract_cost) || 0) * 100) / 100;
        subItem.pricing_mode = "subcontract";
        setForm(f => ({ ...f, line_items: [...f.line_items, subItem] }));
        return;
      }
      // iter235 — flat-rate service items: single labor-classed line at flat_rate/unit.
      if (mp.pricing_mode === "flat_rate") {
        const flat = Math.round((parseFloat(mp.flat_rate) || 0) * 100) / 100;
        const flatItem = emptyLineItem("labor", mp.name, 1, flat, mp.part_number || "", mp.manufacturer || "");
        setForm(f => ({ ...f, line_items: [...f.line_items, flatItem] }));
        return;
      }
      // Calculate GM%-based prices
      const matGM = mp.material_margin ?? 50;
      const totalMat = matGM >= 100 ? (mp.part_cost || 0) : (mp.part_cost || 0) / (1 - matGM / 100);
      const labGM = mp.labor_margin ?? 73.333;
      const ratePerHr = labGM >= 100 ? (mp.labor_cost ?? 40) : Math.ceil((mp.labor_cost ?? 40) / (1 - labGM / 100));
      // Add material line with GM%-calculated total
      if ((mp.part_cost || 0) > 0) {
        const materialItem = emptyLineItem("material", mp.name, 1, Math.round(totalMat * 100) / 100, mp.part_number || "", mp.manufacturer || "");
        setForm(f => ({ ...f, line_items: [...f.line_items, materialItem] }));
      }
      // Add labor line with GM%-calculated rate
      if ((mp.labor_hours || 0) > 0 && ratePerHr > 0) {
        const laborItem = emptyLineItem("labor", mp.name, mp.labor_hours, Math.round(ratePerHr * 100) / 100, mp.part_number || "", mp.manufacturer || "");
        setTimeout(() => {
          setForm(f => ({ ...f, line_items: [...f.line_items, laborItem] }));
        }, 50);
      }
      // If neither has cost, just add a material line with 0
      if ((mp.part_cost || 0) === 0 && ((mp.labor_hours || 0) === 0 || ratePerHr === 0)) {
        const lineItem = emptyLineItem("material", mp.name, 1, 0, mp.part_number || "", mp.manufacturer || "");
        setForm(f => ({ ...f, line_items: [...f.line_items, lineItem] }));
      }
    } else if (item.id.startsWith("equip:")) {
      const me = item._equip;
      const lineItem = emptyLineItem("equipment", `${me.name}${me.description ? ` - ${me.description}` : ""}`, 1, 0);
      setForm(f => ({ ...f, line_items: [...f.line_items, lineItem] }));
    }
  };
  const updateInspection = (idx, field, value) => {
    const items = [...form.inspection_items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, inspection_items: items });
  };

  const primarySite = clientSites.find(s => s.is_primary) || clientSites[0];

  const defaultSiteId = (selectedSiteId && selectedSiteId !== "all") ? selectedSiteId : (primarySite?.id || "");

  const resetForm = () => {
    // Auto-fill the default Project Manager (if one is flagged in
    // Settings) on every new WO. Existing WOs are unaffected because
    // openEdit overwrites these fields with the WO's own values.
    const defaultPM = (projectManagers || []).find(pm => pm.is_default);
    setForm({
      title: "", description: "", customer_notes: "", internal_notes: "", site_id: defaultSiteId, work_date: getMountainToday(),
      equipment_ids: [], line_items: [emptyLineItem("1", "Labor", 1, 150)],
      tax_rate: 0, hide_tax: clientHideTax || false, project_type: "service", tm_charges: [], tax_breakdown: null, tax_authority: "", customer_po_number: "", cc_fee_pct: defaultCcFeePct, account_manager: localStorage.getItem("abs_current_user") || "", account_manager_email: localStorage.getItem("abs_current_user_email") || "", notes: [], inspection_items: [], wo_number: "", status: "draft", annual_service: false, scheduled_date: "",
      completion_date: "", proposal_id: null, budget: null, change_orders: [], assigned_tech_id: null, assigned_tech_ids: [], lead_tech_id: null, signature_required: true,
      project_manager_id: defaultPM?.id || null, project_manager_name: defaultPM?.name || "",
    });
    setEditingWO(null); setSignedBy("");
    // Reset Qualifications visibility — fresh form starts hidden until user opens it.
    setShowQualifications(false);
  };

  const openNew = async (equipIds = []) => {
    resetForm();
    prevTaxKeyRef.current = null;
    let woNum = "";
    try { const res = await getNextWONumber(clientId); woNum = res.data.wo_number; } catch {}
    // Refetch PMs at WO-open time so a default flagged in Settings AFTER
    // this component mounted still auto-fills. Without this refresh the
    // local state would stay stale until a full page reload.
    let freshPMs = projectManagers;
    try {
      const pmRes = await getProjectManagers();
      freshPMs = pmRes.data || projectManagers;
      setProjectManagers(freshPMs);
    } catch {}
    const equipDetails = clientEquipment.filter(e => equipIds.includes(e.id));
    const lineItems = [];

    equipDetails.forEach(e => {
      const matCost = parseFloat(e.part_cost) || 0;
      const matGM = parseFloat(e.material_margin) ?? 50;
      const totalMat = matGM >= 100 ? matCost : matCost / (1 - matGM / 100);
      const labCost = parseFloat(e.labor_cost) ?? 40;
      const labGM = parseFloat(e.labor_margin) ?? 73.333;
      const ratePerHr = labGM >= 100 ? labCost : Math.ceil(labCost / (1 - labGM / 100));
      const hours = parseFloat(e.labor_hours) || 0;
      const freight = parseFloat(e.freight) || 0;
      const qty = parseInt(e.qty) > 0 ? parseInt(e.qty) : 1;
      const label = e.name + (e.part_name ? ` - ${e.part_name}` : "");

      // Material line — qty mirrors the equipment record's qty so a
      // 2x equipment unit creates a 2-qty material row.
      if (matCost > 0) {
        lineItems.push(emptyLineItem("material", label, qty, Math.round(totalMat * 100) / 100, e.part_number || "", e.manufacturer_name || ""));
      }
      // Labor line — total hours scale with qty (e.g. 2 units × 4 hrs each = 8 total hrs).
      if (hours > 0 && ratePerHr > 0) {
        lineItems.push(emptyLineItem("labor", label, hours * qty, ratePerHr, e.part_number || "", e.manufacturer_name || ""));
      }
      // Freight line — qty mirrors equipment qty.
      if (freight > 0) {
        lineItems.push(emptyLineItem("freight", label, qty, freight, e.part_number || "", e.manufacturer_name || ""));
      }
      // Equipment / Disposal / Travel — LUMP-SUM budgets with GM% markup.
      // Pass-through dollar buckets that do NOT scale with the equipment
      // qty (a $500 disposal sells once whether qty=1 or qty=5). Each
      // lands on the WO at qty=1, unit_price = cost / (1 - gm/100).
      // Pushed only when its cost > 0 so equipment without these buckets
      // stays clean.
      const eCost = parseFloat(e.equipment_cost) || 0;
      const eGM = parseFloat(e.equipment_margin) || 0;
      if (eCost > 0) {
        const ePrice = eGM >= 100 ? eCost : eCost / (1 - eGM / 100);
        lineItems.push(emptyLineItem("equipment", label, 1, Math.round(ePrice * 100) / 100, e.part_number || "", e.manufacturer_name || ""));
      }
      const dCost = parseFloat(e.disposal_cost) || 0;
      const dGM = parseFloat(e.disposal_margin) || 0;
      if (dCost > 0) {
        const dPrice = dGM >= 100 ? dCost : dCost / (1 - dGM / 100);
        lineItems.push(emptyLineItem("disposal", label, 1, Math.round(dPrice * 100) / 100, e.part_number || "", e.manufacturer_name || ""));
      }
      const tCost = parseFloat(e.travel_cost) || 0;
      const tGM = parseFloat(e.travel_margin) || 0;
      if (tCost > 0) {
        const tPrice = tGM >= 100 ? tCost : tCost / (1 - tGM / 100);
        lineItems.push(emptyLineItem("travel", label, 1, Math.round(tPrice * 100) / 100, e.part_number || "", e.manufacturer_name || ""));
      }
      // If no cost data at all, add an equipment line carrying the qty.
      if (matCost === 0 && hours === 0 && freight === 0 && eCost === 0 && dCost === 0 && tCost === 0) {
        lineItems.push(emptyLineItem("equipment", `${e.name}${e.service_type ? " - " + e.service_type : ""}`, qty, 0, e.part_number || "", e.manufacturer_name || ""));
      }
    });

    // Default labor line if no equipment selected
    if (lineItems.length === 0) {
      lineItems.push(emptyLineItem("labor", "Labor", 1, 150));
    }

    const siteId = equipDetails.length > 0 ? (equipDetails[0].site_id || "") : defaultSiteId;
    const inspectionItems = equipDetails.map(e => ({ equipment_id: e.id, equipment_name: e.name, status: "pending", notes: "" }));
    // Internal `description` no longer auto-fills from equipment part
    // descriptions — those carried customer-irrelevant text into the
    // staff-only field. Customer-facing scope summary now belongs in
    // `customer_notes` (filled in manually below the line items).
    // Auto-fill the default Project Manager (if any) — same rule as
    // resetForm so the Equipment → New WO path stays consistent with
    // the plain "+ New WO" path. Uses freshly-fetched PM list so a
    // newly-toggled default from Settings is picked up.
    const defaultPM = (freshPMs || []).find(pm => pm.is_default);
    setForm({
      title: equipDetails.length === 1 ? equipDetails[0].name : "",
      description: "", customer_notes: "", internal_notes: "", site_id: siteId, work_date: getMountainToday(),
      equipment_ids: equipIds, line_items: lineItems, tax_rate: 0, hide_tax: clientHideTax || false, project_type: "service", tm_charges: [], tax_breakdown: null, tax_authority: "", customer_po_number: "", cc_fee_pct: defaultCcFeePct, account_manager: localStorage.getItem("abs_current_user") || "", account_manager_email: localStorage.getItem("abs_current_user_email") || "", site_contact_id: "", notes: [],
      inspection_items: inspectionItems, wo_number: woNum, status: "draft", annual_service: false, scheduled_date: "",
      completion_date: "", proposal_id: null, budget: null, change_orders: [], assigned_tech_id: null, assigned_tech_ids: [], lead_tech_id: null, signature_required: true,
      project_manager_id: defaultPM?.id || null, project_manager_name: defaultPM?.name || "",
    });
    setShowForm(true);
  };

  const openEdit = (wo) => {
    // Set the tax key to match the WO's current site/exempt so the lookup effect is skipped
    // (existing WOs already have their tax_rate saved — no need to re-fetch)
    prevTaxKeyRef.current = wo.site_id ? `${clientId}_${wo.site_id}_` : null;
    setEditingWO(wo);
    setForm({
      title: wo.title, description: wo.description || "", customer_notes: wo.customer_notes || "", internal_notes: wo.internal_notes || "", site_id: wo.site_id || "",
      work_date: wo.work_date || getMountainToday(),
      equipment_ids: wo.equipment_ids || [],
      line_items: wo.line_items?.length > 0 ? wo.line_items : (wo.project_type === "tm" ? [] : [emptyLineItem("labor", "Labor", 1, 150)]),
      retainage_percent: wo.retainage_percent ?? null,
      tax_rate: wo.tax_rate || 0, hide_tax: !!wo.hide_tax, tax_override: wo.tax_override || null, tax_rate_manual: !!wo.tax_rate_manual, project_type: wo.project_type || "service", tm_charges: wo.tm_charges || [], tax_breakdown: wo.tax_breakdown || null, tax_authority: wo.tax_authority || "", customer_po_number: wo.customer_po_number || "", cc_fee_pct: wo.cc_fee_pct == null ? 0 : wo.cc_fee_pct, account_manager: wo.account_manager || "", account_manager_email: wo.account_manager_email || "", site_contact_id: wo.site_contact_id || "", notes: wo.notes || [],
      inspection_items: wo.inspection_items || [], wo_number: wo.wo_number || "",
      status: wo.status || "draft", annual_service: wo.annual_service || false, scheduled_date: wo.scheduled_date ? wo.scheduled_date.slice(0, 10) : "", completion_date: wo.completion_date ? wo.completion_date.slice(0, 10) : "",
      proposal_id: wo.proposal_id || null, budget: wo.budget || null, change_orders: wo.change_orders || [], assigned_tech_id: wo.assigned_tech_id || null, assigned_tech_ids: wo.assigned_tech_ids || [], lead_tech_id: wo.lead_tech_id || null,
      signature_required: wo.signature_required !== undefined ? wo.signature_required : true,
      project_manager_id: wo.project_manager_id || null, project_manager_name: wo.project_manager_name || "",
      // Breakout toggle — must be re-hydrated from the persisted WO so
      // closing/reopening the WO doesn't silently reset it to OFF.
      show_breakdown: !!wo.show_breakdown,
      // Dispatch-card flags — must re-hydrate so the checkboxes reflect
      // what's actually persisted on the WO.
      lift_required: wo.lift_required !== undefined ? !!wo.lift_required : true,
      dot_inspection_required: !!wo.dot_inspection_required,
      // iter270 — the cancelled banner reads these off the form.
      cancellation_reason: wo.cancellation_reason || "",
      cancelled_by: wo.cancelled_by || "",
      cancelled_at: wo.cancelled_at || "",
      field_complete: !!wo.field_complete,
      field_completed_by: wo.field_completed_by || "",
      field_completed_at: wo.field_completed_at || "",
      // iter317 — proposal discount (baked into the lines) + the
      // absorbed-tax record, both internal-only readouts on the totals.
      discount_type: wo.discount_type || "",
      discount_value: wo.discount_value || 0,
      discount_amount: wo.discount_amount || 0,
      tax_absorbed: wo.tax_absorbed || null,
    });
    setSignedBy(wo.signed_by || "");
    loadWoInspections(wo.id);
    // Auto-hide Qualifications block when the WO was saved without any
    // customer-visible notes — keeps the form clean for WOs that don't
    // need them. User can click "+ Add Qualifications" to re-open.
    setShowQualifications(!!(wo.customer_notes && wo.customer_notes.trim()));
    setShowForm(true);
  };

  const [completionPromptWO, setCompletionPromptWO] = useState(null);

  const handleSave = async (statusOverride, cancel) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.site_id && !isPartsOnly) { toast.error("Site is required"); return; }
    if (!form.project_manager_id) { toast.error("A Project Manager is required"); return; }
    if (!form.wo_number) {
      try { const res = await getNextWONumber(clientId); setForm(f => ({ ...f, wo_number: res.data.wo_number })); form.wo_number = res.data.wo_number; } catch { toast.error("Failed to generate WO#"); return; }
    }
    try {
      const payload = {
        ...form, client_id: clientId, subtotal: revisedSubtotal, tax_amount: taxAmount, total,
        is_parts_only: isPartsOnly,
        status: statusOverride || form.status,
        completion_date: (statusOverride === "completed" && !form.completion_date)
          ? new Date().toISOString().slice(0, 10) : form.completion_date,
        line_items: (form.line_items || []).map(li => ({ ...li, total: li.quantity * li.unit_price })),
        change_orders: (form.change_orders || []).map(co => ({
          ...co,
          line_items: (co.line_items || []).map(li => ({ ...li, total: (li.quantity || 0) * (li.unit_price || 0) })),
        })),
      };
      let savedWO;
      if (editingWO) {
        const res = await updateWorkOrder(editingWO.id, payload);
        savedWO = res.data;
        // iter220 — the generic PUT intentionally drops `status`, so a
        // status change (approve / submit / on-hand / schedule /
        // complete) must go through the dedicated PATCH /status endpoint.
        // That endpoint validates the transition graph and fires the
        // side effects (inventory sync, dispatch cleanup, auto-invoice,
        // scheduled_at stamp) that make a scheduled WO actually appear
        // on the dispatch board. Previously scheduling only "looked"
        // done via the optimistic toast — the status never persisted.
        if (statusOverride && statusOverride !== savedWO.status) {
          try {
            const sres = await patchWorkOrderStatus(editingWO.id, statusOverride, cancel);
            savedWO = { ...savedWO, status: sres.data.status };
          } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't change work order status");
            return;
          }
        }
        const msgs = { approved: "Work order approved", submitted: "Work order submitted", on_hand: "Work order marked On Hand", completed: "Work order completed", scheduled: "Work order scheduled", cancelled: "Work order cancelled — scheduled visits removed" };
        toast.success(msgs[statusOverride] || "Work order saved");
        // Completed → offer the Foundation completion push (office/admin only;
        // never automatic — the dialog is the sole trigger).
        if (statusOverride === "completed" && canAccess("integrations.foundation_queue", "write")) {
          setCompletionPromptWO({ ...savedWO, completion_date: payload.completion_date || savedWO.completion_date });
        }
      } else {
        const res = await createWorkOrder(payload);
        savedWO = res.data;
        toast.success("Work order created");
      }
      if (statusOverride) {
        setForm(f => ({ ...f, status: statusOverride, completion_date: (statusOverride === "completed" && !f.completion_date) ? new Date().toISOString().slice(0, 10) : f.completion_date }));
        if (savedWO) setEditingWO(savedWO);
      }
      if (statusOverride === "cancelled") {
        if (!fullPage) { setShowForm(false); resetForm(); }
      } else if (!editingWO && !statusOverride) {
        // New WO just created — open it on its full page (UI sweep batch 5)
        if (savedWO) {
          setEditingWO(savedWO);
          resetWOAutoSaveFirstLoad();
          navigate(`/work-orders/${savedWO.id}`);
        }
      }
      loadWorkOrders();
      return savedWO;
    } catch { toast.error("Failed to save work order"); }
  };

  const handleSign = async (signatureData) => {
    if (!editingWO) return;
    try {
      // Generate executed WO PDF with signature included
      const doc = new jsPDF();
      const wo = editingWO;
      const site = clientSites.find(s => s.id === form.site_id);

      // Unified company header with logo
      let headerY = addCompanyHeader(doc, 10);

      doc.setFontSize(16);
      doc.text(clientName || "Work Order", 14, headerY + 4);
      doc.setFontSize(10);
      doc.text(`WO# ${form.wo_number || ""}`, 14, headerY + 12);
      doc.text(`Title: ${form.title || ""}`, 14, headerY + 18);
      if (site) doc.text(`Site: ${site.name || ""} — ${site.address || ""}, ${site.city || ""} ${site.state || ""} ${site.zip_code || ""}`, 14, headerY + 24);
      doc.text(`Status: Executed`, 14, headerY + 30);
      doc.text(`Date: ${format(getMountainNow(), "MMM d, yyyy")}`, 14, headerY + 36);

      // Customer Notes — appears just below the header block and above
      // the line items table. Optional; only rendered when set.
      let notesShift = 0;
      if (form.customer_notes) {
        const labelY = headerY + 46;
        doc.setFontSize(10); doc.setFont("helvetica", "bold");
        doc.text("Qualifications", 14, labelY);
        doc.setFontSize(9); doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(form.customer_notes, 180);
        doc.text(lines, 14, labelY + 5);
        notesShift = lines.length * 4 + 9;
      }

      // Line items table — honour the WO's show_breakdown toggle. When ON,
      // render Material and Labor as SEPARATE COLUMNS on a single row per item
      // (not extra labor rows). When OFF, collapse to single Total column.
      // #173 (Group H): pass the *full* ccFeeAmount (which is computed
      // against revisedSubtotal = base + approved COs) so each line
      // row's share reflects the same subtotal denominator that drives
      // the displayed ccFeeAmount. Eliminates the proportional split
      // hack that produced different baseFeeShare vs coFeeShare.
      const execRolled = rollUpLineItems(form.line_items || [], !!form.show_breakdown);
      let execPdfItems = applyCCFeeToRolledItems(execRolled, ccFeePct, ccFeeAmount);
      // iter310 — hide-tax WO: no tax line for the customer; the tax is
      // folded into the Material figures pro-rata by material dollars.
      const execHiddenTax = form.hide_tax ? round2(taxAmount) : 0;
      if (execHiddenTax) {
        const folded = foldHiddenTaxIntoRows(execPdfItems, execHiddenTax, { materialKey: "material_total" });
        if (!folded.ok) { toast.error(NO_MATERIAL_MSG); return; }
        execPdfItems = folded.rows;
      }
      const execHead = form.show_breakdown
        ? [["Part #", "Name", "Qty", "Material", "Labor", "Total"]]
        : [["Part #", "Name", "Qty", "Total"]];
      const rows = execPdfItems.map(li => form.show_breakdown
        ? [
            li.part_number || "",
            cleanDescription(li.name || li.description || ""),
            (li.quantity || 1).toString(),
            fmt(li.material_total || 0),
            fmt(li.labor_total || 0),
            fmt(li.total || 0),
          ]
        : [
            li.part_number || "",
            cleanDescription(li.name || li.description || ""),
            (li.quantity || 1).toString(),
            fmt(li.total || 0),
          ]);
      autoTable(doc, {
        startY: headerY + 44 + notesShift,
        head: execHead,
        body: rows,
        theme: "striped",
        styles: { fontSize: 8 },
        headStyles: { fillColor: [51, 65, 85] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: form.show_breakdown
          ? { 0: { cellWidth: 22 }, 2: { cellWidth: 12, halign: "center" }, 3: { cellWidth: 24, halign: "right" }, 4: { cellWidth: 24, halign: "right" }, 5: { cellWidth: 26, halign: "right" } }
          : { 0: { cellWidth: 25 }, 2: { cellWidth: 15, halign: "center" }, 3: { cellWidth: 25, halign: "right" } },
      });

      let y = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      // #173 (Group H): with the full ccFeeAmount baked into line items
      // above, each visible row already includes its share of the fee.
      // The Subtotal label reads `subtotal + ccFeeAmount` so it matches
      // the line-item sum exactly, and the Change Orders / Revised
      // Subtotal lines stay arithmetically consistent without the
      // proportional baseFeeShare / coFeeShare hack.
      doc.text(`Subtotal: ${fmt(subtotal + ccFeeAmount + execHiddenTax)}`, 140, y, { align: "right" });
      if (coSubtotal > 0) {
        y += 6;
        doc.text(`Change Orders: +${fmt(coSubtotal)}`, 140, y, { align: "right" });
        y += 6;
        doc.text(`Revised Subtotal: ${fmt(revisedSubtotal + ccFeeAmount + execHiddenTax)}`, 140, y, { align: "right" });
      }
      if (!form.hide_tax) {
        y += 6;
        doc.text(`Tax (${form.tax_rate || 0}%): ${fmt(taxAmount)}`, 140, y, { align: "right" });
      }
      y += 6;
      doc.setFontSize(12);
      doc.text(`Total: ${fmt(total)}`, 140, y, { align: "right" });

      // Add signature
      y += 12;
      doc.setFontSize(10);
      doc.text("Customer Signature:", 14, y);
      y += 4;
      doc.addImage(signatureData, "PNG", 14, y, 60, 25);
      y += 28;
      doc.text(`Signed by: ${signedBy || "N/A"}`, 14, y);
      y += 5;
      doc.text(`Date: ${format(getMountainNow(), "MMM d, yyyy h:mm a")}`, 14, y);

      const pdfBase64 = doc.output("datauristring");

      const res = await signWorkOrder(editingWO.id, {
        signature_data: signatureData,
        signed_by: signedBy,
        executed_wo_pdf: pdfBase64,
      });
      const signedWO = res.data;
      toast.success("Signed — executed WO saved");
      setEditingWO(signedWO);
      setForm(f => ({ ...f, status: "scheduled" }));
      loadWorkOrders();
      // Open email dialog to send signed WO to client
      setEmailType("signed");
      const contacts = site?.contacts || [];
      const primary = contacts.find(c => c.is_primary && c.email);
      const firstWithEmail = contacts.find(c => c.email);
      setSelectedContactEmail(primary?.email || firstWithEmail?.email || editingWO?.contact_email || "");
      setShowEmailDialog(true);
    } catch (err) { console.error("Sign error:", err); toast.error("Failed to sign"); }
  };

  const handleDelete = async (woId) => {
    try { await deleteWorkOrder(woId); toast.success("Deleted"); loadWorkOrders(); } catch { toast.error("Failed to delete"); }
  };

  // ── Inspection handlers ──
  const handleAddInspection = async () => {
    const template = inspectionTemplates.find(t => t.id === addInspForm.template_id);
    if (!template) { toast.error("Select a template"); return; }
    if (!editingWO) { toast.error("Save the work order first"); return; }
    try {
      const site = clientSites.find(s => s.id === form.site_id);
      const siteAddr = site ? [site.address, site.city, site.state, site.zip_code].filter(Boolean).join(", ") : "";
      const res = await createInspection({
        template_id: template.id,
        template_name: template.name,
        template_sections: template.sections || [],
        scope_name: template.scope_name || "",
        client_id: clientId,
        client_name: clientName,
        work_order_id: editingWO.id,
        wo_number: form.wo_number || "",
        inspector_name: addInspForm.inspector_name.trim(),
        site_name: site?.name || form.site_id || "",
        site_address: siteAddr,
        conducted_date: getMountainToday(),
        status: "in_progress",
        locations: [],
      });
      toast.success("Inspection created");
      setShowAddInspDialog(false);
      setAddInspForm({ template_id: "", inspector_name: "" });
      loadWoInspections(editingWO.id);
    } catch { toast.error("Failed to create inspection"); }
  };

  const handleDeleteInspection = async (inspId) => {
    try {
      await deleteInspection(inspId);
      toast.success("Inspection deleted");
      if (editingWO) loadWoInspections(editingWO.id);
    } catch { toast.error("Failed to delete inspection"); }
  };

  const inspStats = (insp) => {
    let total = 0, flagged = 0;
    (insp.locations || []).forEach(l => (l.responses || []).forEach(r => { total++; if (r.flagged) flagged++; }));
    return { locations: (insp.locations || []).length, total, flagged, passRate: total ? Math.round(((total - flagged) / total) * 100) : 0 };
  };

  // ── Change Order CRUD (inline) ──
  const addChangeOrder = () => {
    const cos = form.change_orders || [];
    const newCO = {
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      co_number: nextCoNumber(form.wo_number || editingWO?.wo_number, cos),
      description: "",
      status: "draft",
      approval_token: null,
      scopes: [],
      line_items: [],
      budget: null,
      subtotal: 0,
      created_at: new Date().toISOString(),
    };
    setForm(prev => ({ ...prev, change_orders: [...(prev.change_orders || []), newCO] }));
    setWoTab("change_orders");
  };

  const updateChangeOrder = (coId, updates) => {
    setForm(prev => ({
      ...prev,
      change_orders: (prev.change_orders || []).map(co => co.id === coId ? { ...co, ...updates } : co),
    }));
  };

  const removeChangeOrder = (coId) => {
    setForm(prev => ({
      ...prev,
      change_orders: (prev.change_orders || []).filter(co => co.id !== coId),
    }));
  };

  const saveCOsToWO = async ({ skipReload = false, silent = false } = {}) => {
    if (!editingWO) return null;
    setCoSaving(true);
    try {
      // SVC-FIX-1 — send ONLY change_orders. Spreading the stored WO doc
      // re-sent stale fields (photos, long PO #, …) that 422'd the save.
      const payload = {
        change_orders: (form.change_orders || []).map(co => ({
          ...co,
          line_items: (co.line_items || []).map(li => ({ ...li, total: (li.quantity || 0) * (li.unit_price || 0) })),
        })),
      };
      await updateWorkOrder(editingWO.id, payload);
      if (!skipReload) {
        await loadWorkOrders();
      }
      if (!silent) toast.success("Change orders saved");
      return editingWO;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Failed to save change orders");
      return null;
    } finally {
      setCoSaving(false);
    }
  };

  const handleSubmitCO = async (woId, coId, coTotalFromBuilder) => {
    // iter212.3 — mirrors handleEmailProposal architecture:
    //   1. Save + submit the CO here (mints the approval token).
    //   2. Open the COEmailDialog with the token pre-loaded.
    //   3. Dialog's "Send CO Link" button fires mailto: on a FRESH user
    //      click (zero awaits before), so Chrome doesn't drop it.
    const saved = await saveCOsToWO({ skipReload: true, silent: true });
    if (!saved) {
      toast.error("Failed to save change orders");
      return null;
    }
    try {
      const res = await submitChangeOrder(saved.id, coId);
      const token = res.data?.token;
      resetWOAutoSaveFirstLoad();
      // Find the freshly-submitted CO for the dialog context.
      const submittedCO = (form.change_orders || []).find(c => c.id === coId);
      setForm(prev => ({
        ...prev,
        change_orders: (prev.change_orders || []).map(co =>
          co.id === coId ? { ...co, status: "submitted", approval_token: token, submitted_at: new Date().toISOString() } : co
        ),
      }));
      // Fire-and-forget WO reload; do NOT await (would delay dialog open).
      loadWorkOrders().catch(() => {});

      // Pre-populate contact picker with site primary (or first w/ email).
      const site = clientSites.find(s => s.id === form.site_id);
      const contacts = site?.contacts || [];
      const primary = contacts.find(c => c.is_primary && c.email);
      const firstWithEmail = contacts.find(c => c.email);
      const pick = primary || firstWithEmail || null;
      setCoEmailSelectedContactId(pick ? (pick.id || `idx-${contacts.indexOf(pick)}`) : "");
      setCoEmailSelectedContactEmail(pick?.email || "");

      // Load dialog context — the token, live-computed amount, and CO.
      setCoEmailContext({
        co: submittedCO ? { ...submittedCO, approval_token: token, status: "submitted" } : null,
        coAmount: typeof coTotalFromBuilder === "number"
          ? coTotalFromBuilder
          : (submittedCO?.total || submittedCO?.subtotal || 0),
        approvalToken: token,
      });
      setShowCOEmailDialog(true);
      return res.data;
    } catch {
      toast.error("Failed to submit change order");
      return null;
    }
  };

  const handleRevertCO = async (woId, coId) => {
    await revertCOToDraft(woId, coId);
    resetWOAutoSaveFirstLoad();
    setForm(prev => ({
      ...prev,
      change_orders: (prev.change_orders || []).map(co =>
        co.id === coId ? { ...co, status: "draft", approval_token: null, submitted_at: null } : co
      ),
    }));
    await loadWorkOrders();
  };

  const handleApproveCO = async (woId, coId) => {
    // SVC-FIX-1 — approval no longer rides on a full-WO save (a stale WO
    // field used to 422 the save and surface as "Failed to approve CO").
    // The CO's own edits travel with the approve call; nothing else.
    const co = (form.change_orders || []).find(c => c.id === coId);
    const res = await approveChangeOrder(woId, coId, co);
    resetWOAutoSaveFirstLoad();
    setForm(prev => ({
      ...prev,
      change_orders: (prev.change_orders || []).map(c =>
        c.id === coId ? { ...c, status: "approved", approved_at: new Date().toISOString(), foundation_sync: res.data?.foundation_sync, foundation_sync_status: res.data?.foundation_sync?.status } : c
      ),
    }));
    await loadWorkOrders();
    return res.data;
  };

  const handleUnapproveCO = async (woId, coId) => {
    await unapproveChangeOrder(woId, coId);
    resetWOAutoSaveFirstLoad();
    setForm(prev => ({
      ...prev,
      change_orders: (prev.change_orders || []).map(co =>
        co.id === coId ? { ...co, status: "submitted", approved_at: null } : co
      ),
    }));
    toast.info("Change order reverted to submitted");
    await loadWorkOrders();
  };

  const handleUnlockedCO = async (coId, data) => {
    resetWOAutoSaveFirstLoad();
    setForm(prev => ({
      ...prev,
      change_orders: (prev.change_orders || []).map(co => (co.id === coId && data?.co ? data.co : co)),
    }));
    await loadWorkOrders();
  };

  const handleNotApproveCO = async (woId, coId) => {
    await notApproveChangeOrder(woId, coId);
    resetWOAutoSaveFirstLoad();
    setForm(prev => ({
      ...prev,
      change_orders: (prev.change_orders || []).map(co =>
        co.id === coId ? { ...co, status: "not_approved", approved_at: null } : co
      ),
    }));
    toast.info("Change order marked as not approved");
    await loadWorkOrders();
  };


  const addEquipmentToWO = (equip) => {
    if (form.equipment_ids.includes(equip.id)) return;
    setForm({ ...form,
      equipment_ids: [...form.equipment_ids, equip.id],
      inspection_items: [...form.inspection_items, { equipment_id: equip.id, equipment_name: equip.name, status: "pending", notes: "" }],
      line_items: [...form.line_items, emptyLineItem("equipment", equip.name)]
    });
  };

  const addPartToWO = (part) => {
    setForm({ ...form, line_items: [...form.line_items, emptyLineItem("material", part.name)] });
  };

  // --- PDF ---
  const generatePDF = (includeSig) => {
    const doc = new jsPDF();
    const site = clientSites.find(s => s.id === form.site_id);
    const money = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

    // Unified company header with logo
    let headerY = addCompanyHeader(doc, 10);

    doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 41, 59);
    doc.text("WORK ORDER", 14, headerY + 6);
    doc.setFont("courier", "bold"); doc.setFontSize(12); doc.setTextColor(29, 78, 216);
    doc.text(`${form.wo_number || "—"}`, 14, headerY + 13);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text(`${STATUS_LABELS[form.status] || form.status}`, 14 + doc.getTextWidth(form.wo_number || "—") + 6, headerY + 13);
    doc.setTextColor(0, 0, 0);

    // Meta grid — mirrors WOPreview: BILL TO | PROJECT, SITE | ADDRESS,
    // DATE | SCHEDULED (labels slate-400 uppercase; right column right-aligned).
    const siteAddr = site ? [site.address, site.city, site.state, site.zip_code].filter(Boolean).join(", ") : "";
    const rightX = 200;
    const label = (txt, x, yy, right) => {
      doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(148, 163, 184);
      doc.text(txt.toUpperCase(), x, yy, right ? { align: "right" } : undefined);
    };
    const value = (txt, x, yy, right, opts = {}) => {
      doc.setFontSize(9.5); doc.setFont("helvetica", "normal"); doc.setTextColor(30, 41, 59);
      doc.text(txt || "—", x, yy, { ...(right ? { align: "right" } : {}), ...opts });
    };
    let gY = headerY + 22;
    label("Bill To", 14, gY); label("Project", rightX, gY, true);
    value(clientName, 14, gY + 5); value(form.title, rightX, gY + 5, true, { maxWidth: 90 });
    gY += 13;
    label("Site", 14, gY); label("Address", rightX, gY, true);
    value(site?.name, 14, gY + 5); value(siteAddr, rightX, gY + 5, true, { maxWidth: 90 });
    gY += 13;
    const workDateStr = form.work_date ? format(new Date(form.work_date + "T00:00:00"), "MMMM d, yyyy") : "—";
    label("Date", 14, gY); value(workDateStr, 14, gY + 5);
    if (form.scheduled_date) {
      label("Scheduled", rightX, gY, true);
      value(format(new Date(form.scheduled_date + "T00:00:00"), "MMMM d, yyyy"), rightX, gY + 5, true);
    }
    // CONTACT — kept on the PDF (customer-facing addressee line).
    const contact = (() => {
      const cid = form.site_contact_id;
      if (!cid) return null;
      const baseContacts = (site?.contacts) || [];
      const extras = (siteContactsOverlay && siteContactsOverlay[form.site_id]) || [];
      const list = [
        ...baseContacts,
        ...extras,
        ...((clientSites || []).flatMap(s => s.contacts || [])),
      ];
      return list.find(c => c.id === cid) || null;
    })();
    gY += 13;
    if (contact) {
      label("Contact", 14, gY);
      value([contact.name, contact.email, contact.phone].filter(Boolean).join(" · "), 14, gY + 5, false, { maxWidth: 180 });
      gY += 13;
    }
    doc.setTextColor(0, 0, 0);

    let y = gY + 2;
    // Internal `form.description` is kept editable in the form for staff
    // notes but intentionally NOT rendered on the customer-facing PDF or
    // preview. Use Customer Notes (Qualifications) for customer text.
    if (form.customer_notes) {
      doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(51, 65, 85);
      doc.text("QUALIFICATIONS", 14, y);
      y += 5;
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(71, 85, 105);
      const lines = doc.splitTextToSize(form.customer_notes, 180);
      doc.text(lines, 14, y);
      doc.setTextColor(0, 0, 0);
      y += lines.length * 4.5 + 4;
    }

    y += 2;
    // Line items — preview parity: slate-700 header, striped rows, mono
    // part # with "—" placeholder, Subtotal footer row (slate-100).
    // #173 (Group H): pass the full ccFeeAmount (computed against
    // revisedSubtotal = base + approved COs) so each line row's share
    // reflects the same subtotal denominator as the displayed total.
    const viewRolled = rollUpLineItems(form.line_items || [], !!form.show_breakdown);
    let pdfItems = applyCCFeeToRolledItems(viewRolled, ccFeePct, ccFeeAmount);
    // iter310 — hide-tax WO: the customer's copy carries the tax inside
    // Material and shows no tax line.
    const pdfHiddenTax = form.hide_tax ? round2(taxAmount) : 0;
    if (pdfHiddenTax) {
      const folded = foldHiddenTaxIntoRows(pdfItems, pdfHiddenTax, { materialKey: "material_total" });
      if (!folded.ok) { toast.error(NO_MATERIAL_MSG); return; }
      pdfItems = folded.rows;
    }
    const pdfHead = form.show_breakdown
      ? [["Part #", "Name", "Qty", "Material", "Labor", "Total"]]
      : [["Part #", "Name", "Qty", "Total"]];
    const pdfBody = pdfItems.map(i => form.show_breakdown
      ? [
          i.part_number || "—",
          cleanDescription(i.name || i.description || "") || "—",
          (i.quantity || 1).toString(),
          money(i.material_total || 0),
          money(i.labor_total || 0),
          money(i.total),
        ]
      : [
          i.part_number || "—",
          cleanDescription(i.name || i.description || "") || "—",
          (i.quantity || 1).toString(),
          money(i.total),
        ]);
    const pdfFoot = form.show_breakdown
      ? [["", "", "", "", "Subtotal", money(subtotal + ccFeeAmount + pdfHiddenTax)]]
      : [["", "", "Subtotal", money(subtotal + ccFeeAmount + pdfHiddenTax)]];
    autoTable(doc, {
      startY: y,
      head: pdfHead,
      body: pdfBody,
      foot: pdfFoot,
      theme: "striped",
      headStyles: { fillColor: [51, 65, 85], fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      footStyles: { fillColor: [241, 245, 249], textColor: [51, 65, 85], fontStyle: "bold", fontSize: 9, halign: "right" },
      columnStyles: form.show_breakdown
        ? { 0: { cellWidth: 22 }, 2: { cellWidth: 12, halign: "center" }, 3: { cellWidth: 24, halign: "right" }, 4: { cellWidth: 24, halign: "right" }, 5: { cellWidth: 26, halign: "right" } }
        : { 0: { cellWidth: 25 }, 2: { cellWidth: 15, halign: "center" }, 3: { cellWidth: 25, halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Change Orders — mirrors WOPreview: every CO renders with an amber
    // header table, rolled items, and CO Subtotal / Tax / CO Total footer.
    const pdfCOs = form.change_orders || [];
    if (pdfCOs.length > 0) {
      doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 41, 59);
      doc.text("CHANGE ORDERS", 14, y);
      y += 4;
      pdfCOs.forEach(co => {
        if (y > 250) { doc.addPage(); y = 15; }
        const coItems = co.line_items || [];
        const coRolled = rollUpLineItems(coItems, !!form.show_breakdown);
        const coSub = co.subtotal != null
          ? co.subtotal
          : coItems.reduce((s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0);
        const coMaterialSub = coItems.reduce((s, li) => {
          const ct = String(li.cost_type || "").toLowerCase();
          if (ct === "2" || ct === "material") return s + (li.quantity || 0) * (li.unit_price || 0);
          return s;
        }, 0);
        const coTaxRate = parseFloat(form.tax_rate) || 0;
        const coTaxAmt = coMaterialSub * (coTaxRate / 100);
        const coGrand = coSub + coTaxAmt;
        doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(180, 83, 9);
        doc.text(`${co.co_number || "CO"}`, 14, y + 4);
        doc.setFont("helvetica", "normal"); doc.setTextColor(71, 85, 105);
        doc.text(co.description || "", 14 + doc.getTextWidth(co.co_number || "CO") + 4, y + 4, { maxWidth: 150 });
        const coBody = coRolled.map(item => form.show_breakdown
          ? [item.part_number || "—", item.name || "—", (item.quantity || 1).toString(), money(item.material_total || 0), money(item.labor_total || 0), money(item.total)]
          : [item.part_number || "—", item.name || "—", (item.quantity || 1).toString(), money(item.total)]);
        const span = form.show_breakdown ? 5 : 3;
        // iter310 — hide-tax WO: every CO's tax is already carried inside
        // the main table's Material figures, so the CO block shows no tax.
        const coFoot = form.hide_tax
          ? [[...Array(span - 1).fill(""), "CO Total", money(coSub)]]
          : [
            [...Array(span - 1).fill(""), "CO Subtotal", money(coSub)],
            ...(coTaxRate > 0 && coMaterialSub > 0 ? [[...Array(span - 1).fill(""), `Tax (${coTaxRate}% on material)`, money(coTaxAmt)]] : []),
            [...Array(span - 1).fill(""), "CO Total", money(coGrand)],
          ];
        autoTable(doc, {
          startY: y + 7,
          head: pdfHead,
          body: coBody,
          foot: coFoot,
          theme: "striped",
          headStyles: { fillColor: [180, 83, 9], fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          alternateRowStyles: { fillColor: [255, 251, 235] },
          footStyles: { fillColor: [255, 251, 235], textColor: [146, 64, 14], fontStyle: "bold", fontSize: 8.5, halign: "right" },
          columnStyles: form.show_breakdown
            ? { 0: { cellWidth: 22 }, 2: { cellWidth: 12, halign: "center" }, 3: { cellWidth: 24, halign: "right" }, 4: { cellWidth: 24, halign: "right" }, 5: { cellWidth: 26, halign: "right" } }
            : { 0: { cellWidth: 25 }, 2: { cellWidth: 15, halign: "center" }, 3: { cellWidth: 25, halign: "right" } },
        });
        y = doc.lastAutoTable.finalY + 6;
      });
      y += 2;
    }

    // Grand totals — matches WOPreview labels exactly.
    if (y > 250) { doc.addPage(); y = 15; }
    doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
    doc.text("Original Subtotal:", 150, y, { align: "right" });
    doc.setTextColor(30, 41, 59);
    doc.text(money(subtotal + ccFeeAmount + pdfHiddenTax), 200, y, { align: "right" });
    if (coSubtotal > 0) {
      y += 6; doc.setTextColor(180, 83, 9);
      doc.text("Change Orders:", 150, y, { align: "right" });
      doc.text(`+${money(coSubtotal)}`, 200, y, { align: "right" });
      y += 6; doc.setTextColor(30, 41, 59);
      doc.text("Revised Subtotal:", 150, y, { align: "right" });
      doc.text(money(revisedSubtotal + ccFeeAmount + pdfHiddenTax), 200, y, { align: "right" });
    }
    if (!form.hide_tax && form.tax_rate > 0 && taxAmount > 0) {
      y += 6; doc.setTextColor(100, 116, 139);
      doc.text(`Tax (${form.tax_rate}% on material):`, 150, y, { align: "right" });
      doc.setTextColor(30, 41, 59);
      doc.text(money(taxAmount), 200, y, { align: "right" });
    }
    y += 8; doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(15, 23, 42);
    doc.text("Total:", 150, y, { align: "right" }); doc.text(money(total), 200, y, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setTextColor(0, 0, 0);

    if (form.inspection_items.length > 0) {
      y += 14; doc.setFontSize(11); doc.text("Equipment Inspection", 14, y); y += 6;
      autoTable(doc, {
        startY: y,
        head: [["Equipment", "Status", "Notes"]],
        body: form.inspection_items.map(i => [i.equipment_name, i.status.charAt(0).toUpperCase() + i.status.slice(1), i.notes || "-"]),
        theme: "striped", headStyles: { fillColor: [51, 65, 85], fontSize: 9 }, bodyStyles: { fontSize: 9 },
      });
      y = doc.lastAutoTable.finalY;
    }

    // Include signature if available
    const sigData = includeSig ? (editingWO?.signature_data) : null;
    if (sigData) {
      y += 12; doc.setFontSize(11); doc.setFont("helvetica", "bold");
      doc.text("Customer Signature", 14, y); y += 8;
      try { doc.addImage(sigData, "PNG", 14, y, 60, 25); } catch {}
      y += 28; doc.setFontSize(9); doc.setFont("helvetica", "normal");
      const signedByName = editingWO?.signed_by || signedBy || "N/A";
      const signedAtDate = editingWO?.signed_at ? format(new Date(editingWO.signed_at), "MMM d, yyyy h:mm a") : "";
      doc.text(`Signed by: ${signedByName}${signedAtDate ? ` on ${signedAtDate}` : ""}`, 14, y);
    }

    return doc;
  };

  const downloadPDF = (doc, filename) => {
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const openEmailDialog = (type) => {
    setEmailType(type);
    const site = clientSites.find(s => s.id === form.site_id);
    const contacts = site?.contacts || [];
    // Auto-select: primary contact first, then first contact with email, then WO's stored contact
    const primary = contacts.find(c => c.is_primary && c.email);
    const firstWithEmail = contacts.find(c => c.email);
    const woContact = editingWO?.contact_email || "";
    setSelectedContactEmail(primary?.email || firstWithEmail?.email || woContact || "");
    setShowEmailDialog(true);
  };

  const [includeWOSignLink, setIncludeWOSignLink] = useState(true);
  const [confirmCancelWO, setConfirmCancelWO] = useState(false);
  const [quickWOOpen, setQuickWOOpen] = useState(false);

  const handleSendEmail = async () => {
    const isSigned = emailType === "signed";

    if (!isSigned) {
      // For submit flow: save with submitted status first
      const saved = await handleSave("submitted");
      if (!saved) return;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_URL;

    // Build primary link
    let wo_link = "";
    if (!isSigned && includeWOSignLink && editingWO?.id) {
      // Not-yet-signed WO: include signable review page so customer can approve online.
      // Prefer the short `/r/w/{code}` URL when the backend mints a code;
      // fall back to the long `/wo-review/{token}` URL so the email never
      // ships without a working link.
      try {
        const res = await generateWOReviewToken(editingWO.id);
        const token = res.data?.token;
        const code = res.data?.short_code;
        if (code) {
          wo_link = `${baseUrl}/api/r/w/${code}`;
        } else if (token) {
          wo_link = `${window.location.origin}/wo-review/${token}`;
        }
      } catch { /* skip link if token gen fails */ }
    } else if (isSigned && (editingWO?.signed_at || editingWO?.executed_wo_pdf) && editingWO?.id) {
      // Signed WO: customer clicks the URL → backend serves the stored
      // executed PDF inline. Try to mint/fetch a short_code so the link
      // is friendly; fall back to the long executed-pdf URL on any error
      // (the long URL always works because the endpoint resolves by id).
      try {
        const res = await ensureWOShortCode(editingWO.id);
        const code = res.data?.short_code;
        if (code) {
          wo_link = `${baseUrl}/api/r/w/${code}`;
        } else {
          wo_link = `${baseUrl}/api/work-orders/${editingWO.id}/executed-pdf`;
        }
      } catch {
        wo_link = `${baseUrl}/api/work-orders/${editingWO.id}/executed-pdf`;
      }
    }

    const woDocName = docFilename(isSigned ? "Signed_Work_Order" : "Work_Order", form.wo_number);
    wo_link = withDocName(wo_link, woDocName);
    const attach = emailDocMode === "attach";
    if (attach) {
      if (isSigned && editingWO?.id) downloadPdfUrl(`/work-orders/${editingWO.id}/executed-pdf`, { suggestedName: woDocName, label: "signed work order" }).catch(() => {});
      else downloadPDF(generatePDF(false), woDocName);
    }

    setTimeout(() => {
      let subject, body;
      const linkLine = attach ? `\n(${woDocName} attached)` : wo_link ? `\n${wo_link}` : "";
      if (isSigned) {
        subject = encodeURIComponent(`ABS Signed Work Order ${form.wo_number} - ${form.title}`);
        body = encodeURIComponent(
          `Hi,\n\nYour signed Work Order is ready:\nWork Order: ${form.title}\nWO#: ${form.wo_number}${linkLine}\n\nTotal: $${total.toFixed(2)}\n\nThis work order has been approved and signed. We will be scheduling the work shortly and will follow up with confirmed dates.\n\nThank you for your business,\nAssociated Building Specialties, Inc.\n303-293-2096`
        );
      } else {
        subject = encodeURIComponent(`ABS Work Order ${form.wo_number} - ${form.title}`);
        const actionText = attach ? "Your Work Order is attached" : includeWOSignLink ? "Please review and approve your Work Order here" : "Your Work Order is ready to view here";
        body = encodeURIComponent(
          `Hi,\n\n${actionText}:\nWork Order: ${form.title}\nWO#: ${form.wo_number}${linkLine}\n\nTotal: $${total.toFixed(2)}\n\nPlease review and let us know if you have any questions.\n\nThank you,\nAssociated Building Specialties, Inc.\n303-293-2096`
        );
      }
      window.location.href = `mailto:${selectedContactEmail || ""}?subject=${subject}&body=${body}`;
    }, 200);

    setShowEmailDialog(false);
    toast.success(attach ? "PDF downloaded — attach it to the email that just opened" : isSigned ? "Email opened with signed Work Order link" : "Email opened with Work Order link");
  };

  const handleDownloadPDF = () => {
    const includeSig = form.status === "scheduled" || form.status === "completed";
    const doc = generatePDF(includeSig);
    downloadPDF(doc, `${form.wo_number || "WorkOrder"}.pdf`);
    toast.success("PDF downloaded");
  };

  useImperativeHandle(ref, () => ({ openNew, openCopy }));

  const openCopy = async (sourceWO) => {
    resetForm();
    let woNum = "";
    try { const res = await getNextWONumber(clientId); woNum = res.data.wo_number; } catch {}
    setForm({
      title: sourceWO.title || "",
      description: sourceWO.description || "",
      site_id: sourceWO.site_id || primarySite?.id || "",
      work_date: getMountainToday(),
      equipment_ids: sourceWO.equipment_ids || [],
      line_items: sourceWO.line_items?.length > 0
        ? sourceWO.line_items.map(li => emptyLineItem(li.cost_type, li.description, li.quantity, li.unit_price, li.part_number || ""))
        : [emptyLineItem("labor", "Labor", 1, 150)],
      tax_rate: sourceWO.tax_rate || 0,
      hide_tax: !!sourceWO.hide_tax,
      project_type: sourceWO.project_type || "service",
      tax_breakdown: sourceWO.tax_breakdown || null,
      customer_po_number: sourceWO.customer_po_number || "",
      notes: [],
      inspection_items: (sourceWO.inspection_items || []).map(ii => ({ ...ii, status: "pending", notes: "" })),
      wo_number: woNum,
      status: "draft",
      annual_service: sourceWO.annual_service || false,
      scheduled_date: "",
      completion_date: "", proposal_id: null, budget: null, change_orders: [], assigned_tech_id: null, assigned_tech_ids: [], lead_tech_id: null,
    });
    setShowForm(true);
  };

  const fmt = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
  const costBadgeColor = { "1": "bg-blue-100 text-blue-700", "2": "bg-slate-100 text-slate-600", "3": "bg-teal-100 text-teal-700", "4": "bg-purple-100 text-purple-700", "5": "bg-amber-100 text-amber-700", "6": "bg-cyan-100 text-cyan-700", "7": "bg-rose-100 text-rose-700", material: "bg-slate-100 text-slate-600", labor: "bg-blue-100 text-blue-700", equipment: "bg-amber-100 text-amber-700", travel: "bg-green-100 text-green-700", freight: "bg-purple-100 text-purple-700", disposal: "bg-rose-100 text-rose-700" };
  const currentSite = clientSites.find(s => s.id === form.site_id);
  const siteContacts = currentSite?.contacts || [];

  // ===== LIST =====
  if (!showForm) {
    if (fullPage) {
      return (
        <div className="flex justify-center py-16" data-testid="wo-page-loading">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand" />
        </div>
      );
    }
    return (
      <>
      <WOListTable
        activeWorkOrders={activeWorkOrders}
        openEdit={(wo) => navigate(`/work-orders/${wo.id}`)}
        onNew={() => setQuickWOOpen(true)}
        getSiteNameById={getSiteNameById}
        technicians={technicians}
        fmt={fmt}
        handleDelete={handleDelete}
      />
      <QuickWOModal open={quickWOOpen} onOpenChange={setQuickWOOpen} parent={null} clientId={clientId} clientSites={clientSites} technicians={technicians} projectManagers={projectManagers}
        onCreated={(wo) => { loadWorkOrders(); navigate(`/work-orders/${wo.id}`); }} />
      </>
    );
  }

  // ===== FORM =====
  const isEditable = form.status === "draft";
  // People edits (AM, PM, techs) are allowed on any active WO — not just drafts.
  // Same guard the scheduled-date field uses.
  const peopleEditable = form.status !== "completed" && form.status !== "cancelled";
  const siteForHeader = clientSites.find(x => x.id === form.site_id);
  const siteAddressStr = siteForHeader ? [siteForHeader.address, siteForHeader.city, siteForHeader.state, siteForHeader.zip_code].filter(Boolean).join(", ") : "";
  const goBack = () => {
    if (returnTo) { navigate(returnTo); if (onReturnHandled) onReturnHandled(); }
    else if (fullPage && onClose) onClose();
    else { setShowForm(false); resetForm(); }
  };
  const onRenumbered = (wo) => {
    setForm((f) => ({ ...f, wo_number: wo.wo_number, change_orders: wo.change_orders || f.change_orders }));
    setEditingWO((prev) => (prev ? { ...prev, wo_number: wo.wo_number, change_orders: wo.change_orders || prev.change_orders } : prev));
    setWorkOrders((list) => list.map((w) => (w.id === wo.id ? { ...w, wo_number: wo.wo_number } : w)));
    window.dispatchEvent(new Event("wo:invoices-refresh"));
  };

  // Line items block — the Line items tab (full page) and the body of the legacy new-WO form.
  const lineItemsBlock = (
    <div className="space-y-4" data-testid="wo-line-items-block">
      {editingWO && (
        <WOLineItemsActions form={form} handleSave={handleSave} setShowWOPreview={setShowWOPreview} handleDownloadPDF={handleDownloadPDF} openEmailDialog={openEmailDialog} />
      )}
      {/* Add from Equipment Library — extracted to `workOrderTab/WOEquipmentLibraryPicker.jsx`. */}
      <WOEquipmentLibraryPicker isEditable={isEditable} items={librarySearchItems} onAdd={addLibraryItem} />
      {/* Qualifications — customer-visible scope summary. */}
      {!form.proposal_id && (
        <WOQualificationsSection form={form} setForm={setForm} isEditable={isEditable} showQualifications={showQualifications} setShowQualifications={setShowQualifications} />
      )}
      {/* WOs are BOM-driven (Proposal or Equipment) — mid-job additions go through a Change Order. */}
      <WOLineItemsTable form={form} setForm={setForm} isEditable={isEditable} fmt={fmt} />
      <WOApprovedCOLineItems form={form} fmt={fmt} />
      <WOTotalsRow
        form={form} setForm={setForm} isEditable={isEditable}
        subtotal={subtotal} coSubtotal={coSubtotal} revisedSubtotal={revisedSubtotal}
        ccFeeAmount={ccFeeAmount} taxAmount={taxAmount} total={total}
        taxOverride={taxOverride} openTaxOverride={openTaxOverride} closeTaxOverride={closeTaxOverride} setTaxOverride={setTaxOverride}
        manualTax={woTaxOverride} manualTaxStale={woTaxStale}
        onManualTaxChange={(doc) => setForm(prev => ({ ...prev, tax_override: doc?.tax_override || null, tax_rate: doc?.tax_rate ?? prev.tax_rate, tax_rate_manual: !!doc?.tax_rate_manual }))}
        woId={editingWO?.id} fmt={fmt}
      />
      {editingWO && form.project_type === "tm" && (
        <TMChargesPanel woId={editingWO.id} form={form} setForm={setForm} defaultLaborRate={defaultLaborRate} />
      )}
      <WOSignatureBlock form={form} setForm={setForm} editingWO={editingWO} />
      <WOExecutedDoc editingWO={editingWO} setForm={setForm} />
    </div>
  );

  // Split view — two tabs side by side; the same tab is never shown twice (picking it swaps panes).
  const toggleSplit = () => setSplitTab((cur) => (cur ? null : (woTab === "line_items" ? "purchase_orders" : "line_items")));
  const pickLeftTab = (k) => { if (splitTab && k === splitTab) setSplitTab(woTab); setWoTab(k); };
  const pickRightTab = (k) => { if (k === woTab) setWoTab(splitTab); setSplitTab(k); };
  const renderTab = (key, compact = false) => {
    switch (key) {
      case "info":
        return (
          <div className="space-y-4" data-testid="wo-tab-info">
          <div className="rounded-xl border bg-card px-4 py-3" data-testid="wo-status-workflow-card">
            <WOStatusWorkflow form={form} />
          </div>
          <div className={`grid grid-cols-1 gap-4 items-stretch ${compact ? "md:grid-cols-2" : "lg:grid-cols-3"}`}>
            <div className={compact ? "md:col-span-2" : ""}>
            <WOGeneralCard
              form={form} setForm={setForm} isEditable={isEditable} editing={generalEditing} setEditing={setGeneralEditing}
              clientSites={clientSites} clientId={clientId} clientName={clientName} navigate={navigate}
              editingWO={editingWO} setEditingWO={setEditingWO}
              scheduledDateRef={scheduledDateRef} editingCompletionDate={editingCompletionDate} setEditingCompletionDate={setEditingCompletionDate}
              subtotal={subtotal} taxAmount={taxAmount} total={total} loadWorkOrders={loadWorkOrders} siteOptional={isPartsOnly}
              custTaxOverride={custTaxOverride} taxOverride={taxOverride} openTaxOverride={openTaxOverride} closeTaxOverride={closeTaxOverride} setTaxOverride={setTaxOverride}
            />
            </div>
            <WODirectoryCard
              form={form} setForm={setForm} isEditable={isEditable} peopleEditable={peopleEditable}
              clientSites={clientSites} siteContactsOverlay={siteContactsOverlay}
              accountManagers={accountManagers} projectManagers={projectManagers} technicians={technicians}
              onAddContactClick={() => setShowAddContactModal(true)} clientBillingContact={clientBillingContact}
            />
            <WOFinancialSummaryCard
              form={form} fmt={fmt} subtotal={subtotal} coSubtotal={coSubtotal} revisedSubtotal={revisedSubtotal} taxAmount={taxAmount} total={total}
              actuals={fdnActuals}
            />
          </div>
          {/* Budget runs the full width; Foundation actuals feed its Actual columns and the footer pulls them. */}
          <WOBudgetCard
            form={form} actuals={fdnActuals} woId={editingWO?.id}
            actualsFooter={editingWO?.foundation_sp_push_status === "success" && user?.role !== "technician" && (
              <WOFoundationActualsCard woId={editingWO.id} jobNo={editingWO.wo_number} pushed onSnapshot={setFdnActuals} />
            )}
          />
          {/* Notes — office scratchpad, note log and field notes (moved here from Activity). */}
          <section className="rounded-xl border bg-card p-4 space-y-3" data-testid="wo-notes-card">
            <WONoteLog editingWO={editingWO} />
            <WOFieldNotes editingWO={editingWO} />
          </section>
        </div>
        );
      case "line_items":
        return (
          <div data-testid="wo-tab-line-items">{lineItemsBlock}</div>
        );
      case "change_orders":
        return (
          <div data-testid="wo-tab-change-orders">
        <WOChangeOrdersSection
          editingWO={editingWO} form={form} coSubtotal={coSubtotal} hasApprovedCOs={hasApprovedCOs} fmt={fmt}
          addChangeOrder={addChangeOrder} updateChangeOrder={updateChangeOrder} removeChangeOrder={removeChangeOrder}
          handleSaveCO={saveCOsToWO} handleSubmitCO={handleSubmitCO} handleRevertCO={handleRevertCO}
          handleApproveCO={handleApproveCO} handleUnapproveCO={handleUnapproveCO} handleNotApproveCO={handleNotApproveCO} handleUnlockedCO={handleUnlockedCO}
          masterScopes={masterScopes} masterPartsList={masterPartsList}
          clientName={clientName} siteName={siteForHeader?.name || ""} siteAddress={siteAddressStr}
        />
        </div>
        );
      case "purchase_orders":
        return (
          <div data-testid="wo-tab-purchase-orders">
        <PurchaseOrders woId={editingWO.id} woNumber={editingWO.wo_number} projectName={editingWO.title} jobsiteAddress={siteAddressStr} ref={poRefreshRef} />
        </div>
        );
      case "invoices":
        return (
          <div data-testid="wo-tab-invoices">
        <WOInvoicesPanel
          editingWO={editingWO} form={form} clientName={clientName} clientId={clientId} clientCustomerNumber={clientCustomerNumber}
          clientSites={clientSites} clientBillingContact={clientBillingContact} getSiteNameById={getSiteNameById}
        />
        </div>
        );
      case "field":
        return (
          <div className="space-y-6" data-testid="wo-tab-field">
          <WOInspectionsList
            editingWO={editingWO} woInspections={woInspections}
            setAddInspForm={setAddInspForm} setShowAddInspDialog={setShowAddInspDialog}
            inspStats={inspStats} handleDeleteInspection={handleDeleteInspection} loadWoInspections={loadWoInspections}
          />
          <TechInfoSection woId={editingWO.id} />
        </div>
        );
      case "activity":
        return (
          <div data-testid="wo-tab-activity">
          <WOActivityTrail woId={editingWO.id} />
        </div>
        );
      default:
        return null;
    }
  };

  // ── Full page (saved WO): Jobs-style header card + tabs ─────────────────────────
  const fullPageBody = editingWO && (
    <>
      <WOHeaderCard
        form={{ ...form, foundation_sp_push_status: form.foundation_sp_push_status ?? editingWO?.foundation_sp_push_status }}
        editingWO={editingWO} fmt={fmt} total={total}
        clientName={clientName} siteName={siteForHeader?.name || ""} siteCity={[siteForHeader?.city, siteForHeader?.state].filter(Boolean).join(", ")}
        woSaveStatus={woSaveStatus} onBack={goBack} onRenumbered={onRenumbered}
        handleSave={handleSave} requestScheduleDate={requestScheduleDate}
        confirmWOComplete={confirmWOComplete} setConfirmWOComplete={setConfirmWOComplete}
        confirmCancelWO={confirmCancelWO} setConfirmCancelWO={setConfirmCancelWO}
        setForm={setForm}
        onQuickWO={() => setQuickWOOpen(true)}
        splitOn={!!splitTab} onToggleSplit={toggleSplit}
        onFromProposal={() => navigate(`/proposals?client_id=${clientId}${form.site_id ? `&site_id=${form.site_id}` : ""}&origin_wo_id=${editingWO.id}`)}
        onOpenWO={(id) => navigate(`/work-orders/${id}`)}
        parentWO={editingWO.created_from_wo_id ? (workOrders.find((w) => w.id === editingWO.created_from_wo_id) || { id: editingWO.created_from_wo_id, wo_number: "parent WO" }) : null}
        relatedWOs={workOrders.filter((w) => w.created_from_wo_id === editingWO.id)}
      />
      <QuickWOModal open={quickWOOpen} onOpenChange={setQuickWOOpen} parent={{ ...editingWO, ...form, id: editingWO.id, wo_number: editingWO.wo_number }}
        clientId={clientId} clientSites={clientSites} technicians={technicians} projectManagers={projectManagers}
        onCreated={(wo) => { loadWorkOrders(); navigate(`/work-orders/${wo.id}`); }} />

      {/* Tab bar + Split: a second pane with its own tab picker, side by side on wide screens. */}
      <div className={splitTab ? "grid grid-cols-1 xl:grid-cols-2 gap-4" : ""}>
        <div className="relative min-w-0">
          <SubTabs tabs={WO_TABS} active={woTab} onChange={pickLeftTab} testId="wo-detail-tabs" className="!px-0" dense={!!splitTab} />
        </div>
        {splitTab && (
          <div className="relative min-w-0 xl:border-l xl:pl-4">
            <SubTabs tabs={WO_TABS.map((t) => ({ ...t, testId: `${t.testId}-right` }))} active={splitTab} onChange={pickRightTab} testId="wo-detail-tabs-right" className="!px-0" dense />

          </div>
        )}
      </div>

      {splitTab ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start" data-testid="wo-split-panes">
          <div className="min-w-0 space-y-3" data-testid="wo-split-left">{renderTab(woTab, true)}</div>
          <div className="min-w-0 space-y-3 xl:border-l xl:pl-4" data-testid="wo-split-right">{renderTab(splitTab, true)}</div>
        </div>
      ) : renderTab(woTab)}
    </>
  );

  // ── Legacy form: a brand-new WO being created from the customer slideout (saved WOs always open full page) ──
  const legacyBody = (
    <>
      <WOFormHeader
        form={{ ...form, foundation_sp_push_status: form.foundation_sp_push_status ?? editingWO?.foundation_sp_push_status }}
        woSaveStatus={woSaveStatus}
        woId={editingWO?.id}
        onRenumbered={onRenumbered}
        onBack={goBack}
      />
      <div className="space-y-4">
        <WOSourceProposalBadge form={form} editingWO={editingWO} clientId={clientId} navigate={navigate} />
        <WODatesRow
          form={form} setForm={setForm} isEditable={isEditable} clientSites={clientSites}
          scheduledDateRef={scheduledDateRef} editingCompletionDate={editingCompletionDate} setEditingCompletionDate={setEditingCompletionDate}
          editingWO={editingWO} setEditingWO={setEditingWO} clientId={clientId}
          subtotal={subtotal} taxAmount={taxAmount} total={total} loadWorkOrders={loadWorkOrders} siteOptional={isPartsOnly}
        />
        <WOAddressLink clientSites={clientSites} siteId={form.site_id} />
        <div className="border-t pt-3">
          <WOFlagsBar
            form={form} setForm={setForm} isEditable={isEditable} woId={editingWO?.id}
            custTaxOverride={custTaxOverride} hiddenTaxAmount={taxAmount}
            taxOverride={taxOverride} openTaxOverride={openTaxOverride} closeTaxOverride={closeTaxOverride} setTaxOverride={setTaxOverride}
          />
        </div>
        <WOPeopleCluster
          form={form} setForm={setForm} isEditable={isEditable} peopleEditable={peopleEditable}
          clientSites={clientSites} siteContactsOverlay={siteContactsOverlay}
          accountManagers={accountManagers} projectManagers={projectManagers} technicians={technicians}
          onAddContactClick={() => setShowAddContactModal(true)}
        />
        <CustomerNotesPanel clientId={clientId} />
        <div className="border-t pt-4">
          {lineItemsBlock}
        </div>
        <WOInternalNotes form={form} setForm={setForm} />
        <WOActionsRow
          form={form} editingWO={editingWO}
          confirmCancelWO={confirmCancelWO} setConfirmCancelWO={setConfirmCancelWO}
          woSaveStatus={woSaveStatus} setShowForm={setShowForm} resetForm={resetForm}
          handleSave={handleSave} setShowWOPreview={setShowWOPreview} handleDownloadPDF={handleDownloadPDF} openEmailDialog={openEmailDialog}
          confirmWOComplete={confirmWOComplete} setConfirmWOComplete={setConfirmWOComplete}
          scheduledDateRef={scheduledDateRef} setForm={setForm}
        />
      </div>
    </>
  );

  return (
    <div className="space-y-4 pb-24" data-testid="work-order-form">
      {fullPage && editingWO ? fullPageBody : legacyBody}

      <FoundationCompletionPrompt
        wo={completionPromptWO}
        open={!!completionPromptWO}
        onClose={() => setCompletionPromptWO(null)}
        onPushed={(res) => setForm((f) => ({ ...f, foundation_sp_push_log_id: res.log_id, foundation_sp_push_status: res.outcome }))}
      />

      {/* Add Contact Modal — creates a contact on the selected site so
          it becomes available as the Site Contact on the WO (and future
          WOs). Extracted to `workOrderTab/WOAddContactDialog.jsx`. */}
      <WOAddContactDialog
        open={showAddContactModal}
        onOpenChange={setShowAddContactModal}
        clientId={clientId}
        siteId={form.site_id}
        onContactAdded={(saved) => {
          setSiteContactsOverlay((prev) => ({
            ...prev,
            [form.site_id]: [...(prev[form.site_id] || []), saved],
          }));
          setForm((prev) => ({ ...prev, site_contact_id: saved.id }));
        }}
      />

      {/* Email Dialog — extracted to
          `workOrderTab/WOEmailDialog.jsx`. */}
      <WOEmailDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        emailType={emailType}
        siteContacts={siteContacts}
        selectedContactEmail={selectedContactEmail}
        setSelectedContactEmail={setSelectedContactEmail}
        includeWOSignLink={includeWOSignLink}
        setIncludeWOSignLink={setIncludeWOSignLink}
        docMode={emailDocMode} setDocMode={setEmailDocMode} woNumber={form.wo_number}
        editingWO={editingWO}
        onSend={handleSendEmail}
      />

      {/* CO Email Dialog — mirrors ProposalEmailDialog. Opens after
          save+submit completes so the "Send CO Link" button fires
          mailto: on a FRESH user click with ZERO awaits before it. */}
      <COEmailDialog
        open={showCOEmailDialog}
        onOpenChange={setShowCOEmailDialog}
        co={coEmailContext?.co || null}
        coAmount={coEmailContext?.coAmount || 0}
        approvalToken={coEmailContext?.approvalToken || ""}
        woNumber={form.wo_number}
        woTitle={form.title}
        clientName={clientName}
        siteName={currentSite?.name || ""}
        siteAddress={currentSite ? [currentSite.address, currentSite.city, currentSite.state, currentSite.zip_code].filter(Boolean).join(", ") : ""}
        siteContacts={siteContacts}
        taxRate={form.tax_rate}
        selectedContactEmail={coEmailSelectedContactEmail}
        setSelectedContactEmail={setCoEmailSelectedContactEmail}
        selectedContactId={coEmailSelectedContactId}
        setSelectedContactId={setCoEmailSelectedContactId}
      />

      {/* Add Inspection Dialog — extracted to
          `workOrderTab/WOAddInspectionDialog.jsx`. */}
      <WOAddInspectionDialog
        open={showAddInspDialog}
        onOpenChange={setShowAddInspDialog}
        form={addInspForm}
        setForm={setAddInspForm}
        templates={inspectionTemplates}
        technicians={technicians}
        onConfirm={handleAddInspection}
      />

      {/* WO Preview Modal */}
      {showWOPreview && (
        <WOPreview
          form={form}
          clientName={clientName}
          siteName={currentSite?.name || ""}
          siteAddress={currentSite ? [currentSite.address, currentSite.city, currentSite.state, currentSite.zip_code].filter(Boolean).join(", ") : ""}
          subtotal={subtotal}
          coSubtotal={coSubtotal}
          taxAmount={taxAmount}
          total={total}
          ccFeePct={ccFeePct}
          ccFeeAmount={ccFeeAmount}
          editingWO={editingWO}
          onClose={() => setShowWOPreview(false)}
        />
      )}
    </div>
  );
});

export default WorkOrderTab;
